import {
  createTranslationMessages,
  createTranslationRef,
} from '@backstage/frontend-plugin-api';
import { TranslationBlueprint } from '@backstage/plugin-app-react';

/**
 * Default (English) messages for the Odoo environments card and for the
 * per-branch page it links to.
 *
 * One register for both on purpose: the page is the card's row opened up, so
 * the two say "Health", "Sync" or "Address" with the very same words. Splitting
 * them into two refs would let those words drift.
 *
 * These live here rather than in @waadoo/backstage-i18n because that package
 * imports translation refs from published plugin packages, and this card is
 * part of the app itself — the dependency would be circular. If the card ever
 * becomes a standalone plugin, both halves can move there.
 */
export const projectEnvironmentsTranslationRef = createTranslationRef({
  id: 'waadoo-project-environments',
  messages: {
    // On a phone (2026-10-29). A branch's line of the environments card:
    // its usage, a short name before a small gauge and the share used; a
    // sleeping branch's state, and who may wake it.
    'phone.cpu': 'CPU',
    'phone.memory': 'Mem.',
    'phone.sleeping': 'asleep',
    'phone.wakeAdmins': 'woken by admins',
    'phone.wakeEditors': 'woken by editors',
    // The header's « Nouvelle branche » as an icon alone: its name and
    // tooltip. The slot under the list: its count alone.
    'phone.newBranch': 'New ephemeral branch, from {{source}}',
    'phone.quota': '{{used}}/{{max}}',
    // The project's total under the list, on one line after the resources
    // glyph (`resources.total` / `resources.partial` its hidden name).
    'phone.totalLine': 'CPU {{cpu}} · Mem. {{memory}} · Vol. {{volumes}}',
    // Before it when some environment could not be read.
    'phone.partialLabel': 'Readable environments only:',
    // The branch page's « ⋯ » button and the bottom sheet it opens: the
    // header's other actions.
    'phone.moreActions': 'More actions',
    'phone.sheetTitle': 'Branch actions',
    'phone.close': 'Close',
    'card.title': 'Environments',
    'card.subtitle': 'One {{app}} environment per development branch',
    'card.refresh': 'Refresh',
    'card.empty':
      'No environment for this project. Push a `dev-…` branch to create one.',
    'card.emptyDelay':
      'The environments of a project created moments ago can take a few minutes to appear.',
    // The count next to the card's title, shown only when a permanent
    // environment is asleep (`permanentEnvironments.ts`). A `_one`/`_other`
    // pair, as `backups.contentDatabases`: the permanent count is never 0
    // when shown (the sleeping branch is one), and English and French agree
    // on 1 and on 2 and more. The deployed count is 0 only for a project
    // whose dev has no Application either — then « 0 déployées », the
    // English plural rule, a known wart.
    'card.permanentCount_one': '{{count}} permanent branch',
    'card.permanentCount_other': '{{count}} permanent branches',
    'card.deployedCount_one': '{{count}} deployed',
    'card.deployedCount_other': '{{count}} deployed',
    // A permanent environment declared by the project's appenv repository
    // and not deployed (`permanentEnvironments.ts`): its pill, wherever it is
    // listed, after a decorative moon.
    'sleeping.badge': 'Asleep',
    // Informational, in place of the address of a sleeping environment: who
    // may wake it (the « Réveiller » button of its row).
    'sleeping.activatableByEditors': 'can be woken by the editors',
    'sleeping.activatableByAdmins': 'can be woken by the administrators',
    // The tag after `sleeping.activatableByAdmins`, for the production.
    'sleeping.adminTag': 'admin',
    // The tier of a branch in full (`branchTier`, `TIER_DESCRIPTION_KEYS`):
    // the tooltip of its environment tag (`EnvironmentTag`, BranchLabel.tsx)
    // and, in the environments card's rows, the branch page's header and the
    // promotion chain, hidden words after its name.
    'tier.productionDescription': 'Production',
    'tier.stagingDescription': 'Pre-production',
    'tier.developmentDescription': 'Integration branch',
    'tier.ephemeralDescription': 'Ephemeral branch',
    // The branch page of a sleeping environment.
    'sleeping.title': 'This environment is asleep',
    'sleeping.description':
      'The branch {{branch}} is a permanent branch of the project, but no {{app}} environment is deployed for it.',
    // Its latest pipeline, when the forge has one: the run's number, then its
    // status (`ci.*`).
    'sleeping.pipeline': 'Pipeline #{{number}} · {{status}}',
    'sleeping.pipelineNoNumber': 'Latest pipeline · {{status}}',
    // The address the environment will have, read off a deployed one
    // (`previewAppUrl`): its tooltip and accessible description.
    'sleeping.plannedAddress': 'Planned address',
    'sleeping.plannedAddressLabel': 'Planned address: {{address}}',
    // Waking and putting to sleep a permanent environment
    // (`environmentPower.ts`, `EnvironmentPowerDialogs.tsx`): the buttons,
    // the lock of an editor on the production, and the rights line of the
    // sleeping branch page.
    'envPower.wake': 'Wake up',
    'envPower.wakeBranch': 'Wake up {{branch}}',
    'envPower.sleep': 'Put to sleep',
    'envPower.adminsOnly': 'Reserved for administrators',
    // The locked « Put to sleep » of dev (the backend refuses it).
    'envPower.devLocked':
      'dev cannot be put to sleep: its ephemeral branches depend on it',
    'envPower.rightsLine':
      'Project editors and administrators. The production: administrators only.',
    'envPower.cancel': 'Cancel',
    'envPower.launchFailed': 'The task could not be started: {{message}}',
    'envPower.confirmLabel': 'Type {{confirmation}} to confirm',
    // The wake dialog (mockups envs-D1 and sleep-WK).
    'wake.title': 'Wake up {{branch}}',
    'wake.subtitleEditors': '{{project}} · for the project’s editors',
    'wake.subtitleAdmins': '{{project}} · for administrators',
    'wake.createdTitle': 'What will be created',
    'wake.itemEnvironment': 'Environment {{namespace}}',
    'wake.itemEnvironmentDetail': 'namespace, ArgoCD application, network',
    'wake.itemDatabase': 'PostgreSQL database and {{app}} files',
    'wake.itemDatabaseDetail': 'created empty, then filled as chosen below',
    // An environment whose media are on a retained S3 bucket
    // (`xovhs3bucket-media.yaml`): no files created, its media bucket said.
    'wake.itemDatabaseOnly': 'PostgreSQL database of {{app}}',
    'wake.itemDatabaseOnlyDetail': 'created empty, then filled as chosen below',
    'wake.itemMedia': 'Media',
    'wake.itemMediaKept': 'S3 bucket kept, taken back as it is',
    'wake.itemMediaEmptied': 'S3 bucket kept, emptied before the activation',
    'wake.itemBackups': 'Backups',
    'wake.itemBackupsDetail':
      'the existing S3 bucket taken back, daily schedule',
    'wake.itemAddress': 'Address',
    'wake.dataTitle': 'Starting data',
    'wake.dataLatest': 'Restore the latest backup of {{branch}}',
    'wake.dataLatestDetail':
      'the most recent one if it exists — the one taken when it was put to sleep; otherwise the new database is kept',
    'wake.dataFresh': 'New database',
    'wake.dataFreshDetail':
      'empty installation (demonstration data as the project is set up)',
    'wake.duration':
      'Duration: 5 to 10 min, plus the first pipeline if the branch has no image yet. Followed in « Tasks ».',
    // The duration of a `standard` type's wake: no pipeline of its own. An
    // estimate, not measured.
    'wake.durationStandard': 'Duration: a few minutes. Followed in « Tasks ».',
    'wake.submit': 'Wake up {{branch}}',
    // The sleep dialog (mockup sleep-SL).
    'sleep.title': 'Put {{branch}} to sleep',
    'sleep.subtitle': '{{project}} · editors (prod: administrators)',
    'sleep.stepBackupTitle': 'Final backup',
    'sleep.stepBackupText':
      'of the database and files to the S3 bucket, checked before going on',
    'sleep.stepStopTitle': 'Stop:',
    'sleep.stepStopText':
      '{{app}}, PostgreSQL and Valkey deleted, nothing reserved on the cluster any more',
    'sleep.stepKeptTitle': 'Kept:',
    'sleep.stepKeptText':
      'the branch {{branch}}, its CI, its configuration (capacity, HA, volumes) and all its backups',
    'sleep.bucketNote':
      'The backup bucket of {{branch}} is never deleted by the sleep: its backups stay, the most recent being the one of step 1.',
    'sleep.lostWrites':
      'What is changed in {{app}} after the final backup is lost: warn the people using {{branch}} first.',
    'sleep.submit': 'Back up, then put to sleep',
    // The sleep of a type without the `backups` feature (point 8): allowed
    // only without data, which the task checks (backend `sleep.ts`).
    'sleep.stepNoBackupTitle': 'No backup:',
    'sleep.stepNoBackupText':
      'this application type has no backups: the sleep is allowed only for an environment without data (no volume), the task refuses it otherwise',
    'sleep.stepStopTextNoBackups':
      '{{app}} deleted, nothing reserved on the cluster any more',
    'sleep.stepKeptTextNoBackups':
      'the branch {{branch}} and its configuration (capacity, HA)',
    'sleep.submitNoBackups': 'Put to sleep',
    // Shown by the card and the branch page when ArgoCD lists nothing and the
    // id_token carries none of the project's groups (tokenGroups.ts).
    'reauth.title': 'Sign-in needed',
    'reauth.description':
      'Your rights on this project have changed since you signed in (for instance because you just created it). Sign in again to see its environments.',
    'reauth.button': 'Sign in again',
    'column.branch': 'Branch',
    'column.ci': 'CI',
    'column.health': 'Health',
    'column.url': 'Address',
    'column.links': 'Links',
    'ci.success': 'Success',
    'ci.failed': 'Failed',
    'ci.running': 'Running',
    'ci.pending': 'Pending',
    'ci.canceled': 'Canceled',
    'ci.skipped': 'Skipped',
    'ci.unavailable': 'Unavailable',
    // ArgoCD's own health and sync vocabulary. The English side deliberately
    // repeats the API's wording: it is what the ArgoCD UI shows one click away,
    // so anything else would make the two disagree.
    'health.healthy': 'Healthy',
    'health.progressing': 'Progressing',
    'health.degraded': 'Degraded',
    'health.suspended': 'Suspended',
    'health.missing': 'Missing',
    'health.unknown': 'Unknown',
    'sync.synced': 'Synced',
    'sync.outOfSync': 'Out of sync',
    'sync.unknown': 'Unknown',
    'row.openInArgocd': 'Open in ArgoCD',
    'row.openTerminals': 'Open a terminal',
    'row.openInGrafana': 'Monitoring (Grafana)',
    'row.openPipelines': 'CI/CD pipelines for this branch',
    'row.openPipelineRun': 'Open this pipeline run',
    // The globe at the end of the row's links: the environment's application
    // instance, worded as on the home page's project cards.
    'row.openApp': 'Open {{app}} ({{branch}})',
    // Posted when the branch page, reading a running pipeline again, sees it
    // finish.
    'pipeline.succeeded': 'Pipeline succeeded on {{branch}}',
    'pipeline.failed': 'Pipeline failed on {{branch}}',
    // The sync status, shown in the « Health » cell only when it is not
    // « Synced »: its tooltip and accessible name, so that « Unknown » next to
    // the health chip says what it is about.
    'row.syncStatus': 'Sync: {{status}}',
    // The visible text of the branch page's links out to a tool
    // (ExternalToolLink.tsx): the tool's name and nothing else. The full
    // sentence — `backups.openInArgo`, `logs.openInGrafana`… — is the tooltip
    // and the accessible name, and contains this word (Label in Name, WCAG
    // 2.5.3).
    'link.workflows': 'Workflows',
    'link.argocd': 'ArgoCD',
    'link.grafana': 'Grafana',
    'link.pipelines': 'Pipelines',
    // The row of tiles under the branch page's status band (LinkTile.tsx,
    // BranchPage.tsx): each tile's title and subtitle, then the pieces of
    // its accessible name — « <title> — <state> — <action> (new tab) »,
    // the title first (Label in Name, WCAG 2.5.3). « Pipelines » and
    // « ArgoCD » are `link.pipelines` and `link.argocd` above.
    'tile.listLabel': 'Links of this branch',
    'tile.repository': 'Source code',
    'tile.repositorySubtitle': '{{forge}} · {{branch}}',
    'tile.openRepository': 'open the source code of this branch',
    'tile.openPipelines': 'open the CI/CD pipelines of this branch',
    'tile.pipelineRunning': 'Running',
    'tile.pipelineRunningSince': 'Running · for {{elapsed}}',
    'tile.pipelineFailed': 'Latest failed',
    'tile.pipelineRunningName': 'pipeline running',
    'tile.pipelineRunningSinceName': 'pipeline running for {{elapsed}}',
    'tile.pipelineFailedName': 'latest pipeline failed',
    // How long a pipeline has run: under a minute, minutes, then hours and
    // minutes on two digits.
    'tile.elapsedUnderMinute': 'less than a minute',
    'tile.elapsedMinutes': '{{minutes}} min',
    'tile.elapsedHours': '{{hours}} h {{minutes}}',
    'tile.argocdSubtitle': 'Application of the branch',
    'tile.openArgocd': 'open the application of this branch in ArgoCD',
    'tile.monitoring': 'Monitoring',
    'tile.monitoringSubtitle': 'Grafana · {{namespace}}',
    'tile.openMonitoring': 'open the monitoring of this branch in Grafana',
    'terminal.sectionApp': 'Application',
    'terminal.sectionDb': 'Database',
    'terminal.loading': 'Loading pods…',
    'terminal.empty': 'No running pod',
    'terminal.error': 'Pods unavailable',
    // The terminal embedded under the overview's pod list
    // (terminal/PodTerminalPanel.tsx), relayed to ArgoCD by the
    // `waadoo-platform` backend.
    'terminal.open': 'Terminal',
    'terminal.openFor': 'Open a terminal in {{pod}}',
    'terminal.regionLabel': 'Terminal of {{pod}}',
    'terminal.container': 'Container',
    'terminal.containerLabel': 'Container:',
    'terminal.openMarkerOne': '(terminal open)',
    'terminal.openMarkerTwo': '(two terminals open)',
    'terminal.openTooltip': 'Open a terminal',
    'terminal.close': 'Close',
    'terminal.duplicate': 'Duplicate',
    'terminal.duplicateFor': 'Open a second terminal in {{pod}}',
    'terminal.replaced':
      'Two terminals at most: the one in {{pod}} was closed.',
    'terminal.containersLoading': 'Loading containers…',
    'terminal.containersError': 'The containers of this pod cannot be read.',
    'terminal.loadingView': 'Loading the terminal…',
    'terminal.connecting': 'Connecting…',
    'terminal.renewed': 'Session renewed — new shell',
    'terminal.idleWarning': 'Idle session — closing in {{seconds}} s',
    'terminal.keepAlive': 'Keep the session',
    'terminal.restart': 'Restart',
    'terminal.end.ended': 'Session ended',
    'terminal.end.idle': 'Session closed after 20 minutes of inactivity',
    'terminal.end.limitUser':
      'You already have the maximum number of terminals open: close one first.',
    'terminal.end.limitTotal':
      'Too many terminals are open on the platform: try again in a moment.',
    'terminal.end.refused': 'Access to this terminal is refused.',
    'terminal.end.argocdDenied':
      'ArgoCD refuses to open this terminal (insufficient rights).',
    'terminal.end.argocdRejected':
      'ArgoCD could not open the terminal (pod stopped, container missing or terminal disabled).',
    'terminal.end.upstreamError': 'ArgoCD cannot be reached.',
    'terminal.end.shutdown': 'Session interrupted by a Backstage restart.',
    'terminal.end.tokenExpired':
      'Your Authelia session has expired: reload the page to sign in again.',
    'terminal.end.unavailable': 'The terminal cannot be reached.',
    // The notices under a session that never showed a byte (TerminalView):
    // silent for NO_OUTPUT_TIMEOUT_MS, or closed before its first output.
    'terminal.unresponsive': 'The terminal is not responding.',
    'terminal.lost': 'Connection to the terminal lost.',
    'terminal.retry': 'Retry',
    // The per-branch page.
    'page.revision': 'Revision',
    'page.notFound':
      'No environment for branch {{branch}} in project {{project}}. It may have just been deleted.',
    // The first breadcrumb of the project page, back to the home page; on
    // the branch page, the name and tooltip of the house button doing the same.
    'page.homeCrumb': 'Home',
    // The branch page's button back to its project, before the title: its
    // accessible name (the button itself shows the project's name).
    'page.backToProject': 'Back to project {{project}}',
    // The BCP 47 locale every date of the card and the page is formatted in.
    // A message and not the app language: the French messages below override
    // the DEFAULT language, so the app language stays `en` while the screen
    // reads in French — formatting dates in it would print month-first dates
    // (`10/01/2026, 12:00 AM`) in a French page. The locale travels with the
    // words it sits among.
    'format.locale': 'en',
    // The summary band under the header, always visible whatever the tab.
    'band.openApp': 'Open {{app}}',
    // Plural on purpose: the project's image is shown first, but the others
    // ArgoCD reports (init container, database…) unfold under it.
    'band.images': 'Images',
    'band.lastDeploy': 'Last deployment',
    // The accessible names of the toggle next to the project's image, which
    // unfolds every image by its full reference. Closed with images folded, it
    // shows "+N", and the name starts with those very characters (Label in
    // Name, WCAG 2.5.3); `count` is that N. Closed with none folded, it shows
    // "…" and only spells the references out. Open, it shows "−".
    'band.otherImages_one': '+{{count}} other image',
    'band.otherImages_other': '+{{count}} other images',
    'band.fullReferences': 'Full image references',
    'band.hideImages': 'Hide the image references',
    // The CI value while the forge has not answered. Words and not a skeleton,
    // so that the field is never empty when the band is copied or read aloud.
    'band.ciLoading': 'Loading…',
    // The tabs of the per-branch page. `tab.listLabel` names the tab list for
    // screen readers; nothing displays it.
    'tab.listLabel': 'Sections of this environment',
    'tab.overview': 'Overview',
    'tab.logs': 'Logs',
    'tab.emails': 'Emails',
    'tab.data': 'Data',
    'tab.history': 'History',
    'tab.access': 'Access',
    'tab.configuration': 'Configuration',
    // The Overview tab.
    'overview.pods': 'Pods',
    'overview.columnPod': 'Pod',
    // A pod's friendly name (podNames.ts): a CNPG instance by its number,
    // and a label with the pod's own short id, as the logs and upload
    // pickers show it. The product names (Odoo, Valkey…) stay as they are.
    'podName.postgres': 'PostgreSQL {{instance}}',
    // The pod of MariaDB's init Job (the PreSync Job creating its Secret);
    // `label` is the MariaDB component's label.
    'podName.mariadbInit': '{{label}} init Job',
    'podName.withId': '{{label}} · {{id}}',
    'overview.columnActions': 'Actions',
    'overview.columnStarted': 'Started',
    'overview.restarts_one': '{{count}} restart',
    'overview.restarts_other': '{{count}} restarts',
    // The pods card's placement (podPlacement.ts): the HA, HPA, PostgreSQL
    // role and zone badges. The HA counts are 2 at least, so they take no
    // plural form (and are not named `count`, which would make them plural
    // keys). `zoneBadge`: the zone's letter, « a » for eu-west-par-a — the
    // badge shows the letter alone, « Zone a » opens its accessible name.
    'overview.zoneBadge': 'Zone {{letter}}',
    'overview.zoneBadgeUnknown': 'Zone ?',
    'overview.zoneTooltip': '{{zone}} · node {{node}}',
    'overview.zoneUnknown': 'Node {{node}}, no zone stated',
    'overview.zonePending': 'Waiting to be scheduled',
    // The zone badge's accessible name: its label, then its tooltip's sentence.
    'overview.zoneName': '{{label}} — {{text}}',
    'overview.haBadge': 'HA',
    // `Required` / `Preferred`: the Deployment's zone spread
    // (`topologySpreadConstraints` on the zone label), strict or best effort.
    'overview.haAutoscaling':
      'High availability: {{min}} to {{max}} pods (autoscaling)',
    'overview.haAutoscalingRequired':
      'High availability: {{min}} to {{max}} pods (autoscaling) spread across the zones (strict)',
    'overview.haAutoscalingPreferred':
      'High availability: {{min}} to {{max}} pods (autoscaling) spread across the zones when possible',
    'overview.haReplicas': 'High availability: {{pods}} pods',
    'overview.haReplicasRequired':
      'High availability: {{pods}} pods spread across the zones (strict)',
    'overview.haReplicasPreferred':
      'High availability: {{pods}} pods spread across the zones when possible',
    'overview.haInstancesSync':
      'High availability: {{instances}} PostgreSQL instances, synchronous replication',
    'overview.haInstancesAsync':
      'High availability: {{instances}} PostgreSQL instances, asynchronous replication',
    'overview.dbPrimary': 'Primary',
    'overview.dbPrimaryTooltip': 'Main instance: reads and writes',
    'overview.dbReplica': 'Replica · read',
    'overview.dbReplicaTooltip': 'Read-only copy, ready to take over',
    // The chip's accessible name: its label, then its tooltip's sentence.
    'overview.dbRoleName': '{{label}} — {{text}}',
    // The PostgreSQL version badge's tooltip and accessible name: the version
    // read off the image tag (`17`, `18.3`), then the full image reference.
    'overview.postgresVersion': 'PostgreSQL {{version}} · image {{image}}',
    // The « HPA » badge (PodHpaBadge): its bounds, an en dash between them.
    'overview.hpaBadge': 'HPA {{min}}–{{max}}',
    // Its tooltip and accessible name, built from these parts joined with
    // « · »: a part whose value the HPA does not state is left out. `cpu`
    // when both percentages are known, `cpuTarget` / `cpuCurrent` alone.
    'overview.hpaRange': 'Autoscaling: {{min}} to {{max}} pods',
    'overview.hpaCurrent': 'currently {{current}}',
    'overview.hpaCpu': 'CPU {{current}}% (target {{target}}%)',
    'overview.hpaCpuTarget': 'CPU target {{target}}%',
    'overview.hpaCpuCurrent': 'CPU {{current}}%',
    'overview.hpaScaleDown': 'scales down after {{window}}',
    // The scale-down window: whole minutes as minutes, seconds otherwise.
    'overview.durationMinutes': '{{minutes}} min',
    'overview.durationSeconds': '{{seconds}} s',
    'overview.recentDeployments': 'Latest deployments',
    // The Emails card, alone in the Emails tab: what the environment's Mailpit
    // captured. "Captured" and never "sent" or "received": the point of the
    // card is that none of these reached anyone.
    'mails.title': 'Emails',
    'mails.subtitle':
      'Emails sent by this environment are captured here, not delivered. They disappear when the environment restarts (500 at most).',
    'mails.notConfigured': 'No email capture for this environment.',
    'mails.empty': 'No email captured yet.',
    'mails.unavailable': 'The emails cannot be read right now.',
    'mails.retry': 'Try again',
    // The search above the list. Submitted with Enter, not as one types: see
    // MailsCard.tsx. The operators are Mailpit's own and stay in English.
    'mails.searchLabel': 'Search',
    'mails.searchPlaceholder': 'e.g. subject:quote',
    'mails.searchHint':
      'Free text or operators (from:, to:, subject:), then Enter.',
    'mails.noMatch': 'No email matches.',
    // The table of emails, its headers being `mails.date`, `columnFrom`,
    // `columnTo` and `columnSubject`. Each row's button is named by the
    // sentence below, recipients included: the To column is hidden on a
    // narrow card.
    'mails.listLabel': 'Captured emails',
    'mails.rowLabel': '{{subject}}, from {{from}} to {{to}}, captured {{date}}',
    // The pager under the list: « 1–10 of 57 », with an en dash.
    'mails.pagination': 'Pages of the email list',
    'mails.range': '{{from}}–{{to}} of {{total}}',
    'mails.previous': 'Previous',
    'mails.next': 'Next',
    'mails.columnFrom': 'From',
    'mails.columnTo': 'To',
    'mails.columnSubject': 'Subject',
    'mails.noSubject': '(no subject)',
    'mails.back': 'Back to the list',
    'mails.cc': 'Cc',
    'mails.date': 'Date',
    'mails.attachments': 'Attachments (not downloadable here): {{files}}',
    'mails.viewHtml': 'HTML',
    'mails.viewText': 'Text',
    'mails.frameTitle': 'Email content',
    'mails.emptyBody': '(empty body)',
    'mails.messageGone':
      'This email no longer exists: Mailpit only keeps the 500 most recent.',
    'mails.messageUnavailable': 'This email cannot be read right now.',
    // The header's branch switcher.
    'page.switchBranch': 'Switch branch',
    // The visible text of the switcher's button; `page.switchBranch` stays its
    // accessible name and the menu's.
    'page.switchBranchShort': 'Branch',
    'page.branches': 'Branches',
    // The ephemeral-branch marker (BranchLabel.tsx), on every screen that
    // lists a branch, and the branch page's header and banner: the ghost
    // badge's accessible name and tooltip, and the words the badge shows from
    // 14 idle days, after the ghost. Durations are
    // whole days written "N d", never through a plural form: the app language
    // stays `en` under the French overrides, so a `_one`/`_other` pair would be
    // chosen by English rules.
    'branch.ephemeral': 'Ephemeral branch',
    'branch.ephemeralInactive': 'Ephemeral branch, inactive?',
    'branch.inactive': 'inactive?',
    // {{age}}: `age.*` below.
    'branch.description': 'Ephemeral environment, created {{age}}',
    'branch.descriptionInactive':
      'Ephemeral environment, created {{age}}, inactive for {{idle}} d?',
    'branch.descriptionNoAge': 'Ephemeral environment',
    'branch.descriptionInactiveNoAge':
      'Ephemeral environment, inactive for {{idle}} d?',
    'branch.banner':
      'Test environment: it is deleted with its branch, and its data is not backed up.',
    'page.age': 'Created',
    // How long ago an environment was created (elapsedWords.ts
    // `useAgeWords`), in whole days: after « Created » in the branch page's
    // header, and in the ephemeral badge's sentence (`branch.description`).
    'age.today': 'today',
    'age.yesterday': 'yesterday',
    'age.daysAgo': '{{days}} d ago',
    'history.title': 'Deployment history',
    'history.subtitle':
      'Every revision ArgoCD has deployed on this branch, newest first',
    'history.columnRevision': 'Revision',
    'history.columnDeployedAt': 'Deployed',
    'history.empty': 'No deployment recorded for this branch yet.',
    'history.openRevision': 'Open this revision on the forge',
    'logs.title': 'Logs',
    'logs.subtitle': 'Last {{lines}} lines of {{pod}}, read once',
    // No "loading" message: the panel shows a skeleton while it reads, exactly
    // as the card does. Declaring one here would be a key nothing asks for.
    'logs.noPods': 'No running pod to read logs from.',
    'logs.unavailable':
      'The logs cannot be shown here. Explore them in Grafana.',
    'logs.openInGrafana': 'Explore these logs in Grafana',
    // The button floating over the log box once the reader has scrolled up
    // from its newest line; it takes them back there. The arrow next to it is
    // drawn apart and hidden from screen readers.
    'logs.jumpToLatest': 'Latest logs',
    // The container picker above the log box: every container of every
    // running pod, each named after its pod's friendly name (podNames.ts).
    'logs.container': 'Container',
    'logs.containerOption': '{{pod}} › {{container}}',
    'logs.containerOptionInit': '{{pod}} › {{container}} (init)',
    // The single entry of a pod whose containers could not be listed (or not
    // yet): its logs as ArgoCD gives them without a container named.
    'logs.containerDefault': '{{pod}} › default container',
    // Shown only for a pod whose containers have restarted.
    'logs.previous': 'Before the restart',
    'logs.previousHelp':
      'Logs of the container before its last restart — where the error that stopped it is.',
    'logs.previousUnavailable':
      'No logs from a previous run for this container.',
    // The followed log (useLogStream.ts): the subtitle, then the state shown
    // next to the « Pause » button of the card's header. `logs.subtitle`
    // stays for the previous instance of a container, read once.
    'logs.subtitleLive':
      'Last {{lines}} lines of {{pod}}, then each new line as it is written',
    'logs.pause': 'Pause',
    'logs.resume': 'Resume',
    'logs.statusLive': 'Live',
    'logs.statusPaused': 'Paused',
    'logs.statusReconnecting': 'Reconnecting…',
    // The stream is open and the pod has written nothing yet.
    'logs.waiting':
      'No line yet: new lines will show here as they are written.',
    // The stream stopped for good; the lines already read stay below.
    'logs.podGone':
      'This pod no longer exists: its logs are no longer followed. Refresh the page to read the current pods.',
    'logs.streamStopped':
      'The logs are no longer followed: ArgoCD refused the stream. Refresh the page to try again.',
    // The backups panel. "Listing" throughout, never "backup", for the run
    // itself: what the button starts is an inventory of the bucket, and calling
    // it "a backup" would suggest the click creates one.
    'backups.title': 'Full backups',
    'backups.subtitle': 'Backup sets available for this environment',
    'backups.notConfigured': 'No backup is configured for this environment.',
    'backups.noRun':
      'No listing has been made yet. Start one to see the available backups.',
    'backups.running': 'Listing in progress…',
    'backups.lastRun': 'Last listing: {{date}}',
    'backups.runFailed': 'The last listing failed.',
    // Rendered under the failure above, over the table that a PREVIOUS run
    // produced. It has to name a date: without it the table sits under "the
    // last listing failed" and reads as that run's result.
    'backups.setsFromEarlierRun':
      'Inventory of {{date}}, the last one that succeeded.',
    'backups.openInArgo': 'Open this run in Argo Workflows',
    // The way out when there is no run to name: the first read of the card
    // failed, so it links to the namespace's run list rather than to one run.
    'backups.openNamespaceInArgo':
      'Open the runs of this environment in Argo Workflows',
    // Retries the card's own read, not the workflow: the first read is the only
    // one that leaves nothing on screen, so it is the only one needing a button.
    'backups.retry': 'Try again',
    'backups.noBackups': 'No backup in the bucket for this environment.',
    'backups.rawFallback':
      'This listing did not come back in the expected format. Here it is as it was received.',
    'backups.logPruned':
      'The log of this listing is gone — Argo Workflows only keeps its last twenty runs. Start a new one.',
    'backups.logUnreadable': 'The log of this listing could not be read.',
    // The table: one row per backup set, named by a short ID made of the
    // set's UTC timestamp (`#YYMMDD-HHMM`, see backupSets.ts).
    'backups.columnId': 'ID',
    'backups.columnDate': 'Date',
    'backups.columnContent': 'Contents',
    'backups.columnSize': 'Size',
    'backups.columnActions': 'Actions',
    // The tooltip of the ID, which is read from the timestamp's UTC digits.
    'backups.idUtcTitle': 'UTC time: {{time}}',
    // How long ago, after the date: the browser's own words (`relativeTime`).
    'backups.age': '({{age}})',
    // A `_one`/`_other` pair is safe here although the app language stays
    // `en` under the French overrides (see `branch.ephemeral` below): the
    // count is never 0 — `contentNoDatabase` says that — and English and
    // French agree on 1 and on 2 and more.
    'backups.contentDatabases_one': '{{count}} database',
    'backups.contentDatabases_other': '{{count}} databases',
    'backups.contentVolumes_one': '{{count}} volume',
    'backups.contentVolumes_other': '{{count}} volumes',
    'backups.contentNoDatabase': 'no database',
    'backups.contentNoFile': 'no file',
    // The size units under MiB, which `resources.unitMiB` and
    // `resources.unitGiB` already carry.
    'backups.unitB': 'B',
    'backups.unitKiB': 'KiB',
    // The listing button's accessible name and tooltip; it shows
    // `backups.listingShort`.
    'backups.start': 'Start a listing of the backups',
    // The same button once a run exists. Deliberately NOT `card.refresh`: this
    // one submits a workflow, so it keeps the verb of `backups.logPruned`
    // rather than borrowing the vocabulary of a display refresh.
    'backups.rerun': 'Start a new listing of the backups',
    'backups.listingShort': 'Listing',
    'backups.alreadyRunning':
      'A listing is already running for this environment.',
    // The tooltip of the disabled button, and the answer to a 403 that arrived
    // anyway. Two keys and not one: the first explains a button that cannot be
    // pressed, the second explains a click that was refused, and folding them
    // together would make one of the two read as an error or as advice.
    'backups.submitNotAllowed':
      'Starting a listing is reserved to administrators, and to the project’s editors on dev and its branches.',
    'backups.submitForbidden':
      'You are not allowed to start a listing for this environment.',
    // The tooltip of the listing button while a restore runs — the mirror of
    // `backups.restoreBlockedByListing` below, and worded like it: a wait, not
    // a refusal of the user.
    'backups.listingBlockedByRestore':
      'Wait for the restore in progress to finish before starting a listing.',
    'backups.refreshFailed': 'The listing could not be started.',
    'backups.unavailable': 'The backups cannot be read right now.',
    // The restore, which is the one destructive act of this card. "Restore"
    // throughout for the act; the confirmation says what it overwrites rather
    // than asking "are you sure", which nobody reads.
    'backups.restore': 'Restore',
    // The row's Restore button, named after the set it restores.
    'backups.restoreSet': 'Restore backup {{id}}',
    'backups.restoreConfirmTitle': 'Restore backup {{id}} of {{date}}?',
    'backups.restoreConfirmBody':
      'The databases of the {{namespace}} branch will be overwritten by this backup. A safety backup is taken just before.',
    // The one thing that cannot be guessed from the button. "Not necessarily",
    // because it depends on the environment's XR: the workflow (appbackuptos3
    // >= 1.8.0) stops the workloads when the restore touches a PVC or the XR
    // declares a `stopSelector` (every Odoo skeleton now does); an older
    // appenv with neither leaves them running. The card does not read the
    // XR, so the sentence has to hold for both.
    'backups.restoreNotStopped':
      '{{app}} is not necessarily stopped during the restore: restart the pod once it is over.',
    'backups.restoreConfirmAction': 'Restore for real',
    'backups.restoreCancel': 'Cancel',
    'backups.restoreRunning': 'Restore in progress…',
    // The refusal, not the state: the tooltip of a row's disabled button while
    // a restore is in flight. Worded after `backups.alreadyRunning`, which does
    // the same job for the listing — see the rule stated above
    // `backups.submitNotAllowed`.
    'backups.restoreAlreadyRunning':
      'A restore is already running for this environment.',
    'backups.restoreSucceeded': 'The restore finished.',
    'backups.restoreFailed': 'The last restore failed.',
    // The tooltips of a disabled button, one per reason, and the answer to a
    // 403 that arrived anyway — the same split as `backups.submitNotAllowed`
    // and `backups.submitForbidden` above.
    'backups.restoreNotAllowed':
      'Restoring is reserved to administrators, and to the project’s editors on dev and its branches.',
    'backups.restoreNotConfigured':
      'No restore is configured for this environment.',
    'backups.restoreBlockedByListing':
      'Wait for the listing in progress to finish before restoring.',
    'backups.restoreForbidden':
      'You are not allowed to restore a backup for this environment.',
    'backups.restoreFailedToStart': 'The restore could not be started.',
    'backups.restoreLastRun': 'Last restore: {{date}}',
    // The on-demand backup: the nightly CronWorkflow, run now. "Back up" for
    // this act and never "listing" — this click DOES create a backup, which is
    // exactly the confusion the listing wording above avoids.
    // The accessible name and tooltip of the backup button, which shows
    // `backups.backupShort`.
    'backups.backupNow': 'Back up now',
    'backups.backupShort': 'Back up',
    'backups.backupRunningLabel': 'Backup in progress…',
    'backups.backupConfirmTitle': 'Start a backup of {{namespace}} now?',
    'backups.backupConfirmBody':
      'It is added to the existing backups; nothing is overwritten.',
    'backups.backupConfirmRetention':
      'Backups older than {{days}} days are deleted on this occasion, as with the automatic backup.',
    'backups.backupConfirmRetentionUnknown':
      'Backups beyond the retention period are deleted on this occasion, as with the automatic backup.',
    'backups.backupConfirmAction': 'Start the backup',
    'backups.backupCancel': 'Cancel',
    'backups.backupLastRunning': 'Last backup: running since {{date}}',
    'backups.backupLastSucceeded': 'Last backup: succeeded on {{date}}',
    'backups.backupLastFailed': 'Last backup: failed on {{date}}',
    'backups.backupAlreadyRunning': 'A backup is already running.',
    // The tooltip of the disabled button while a restore runs: a dump taken
    // mid-restore would be a half-restored database filed as a normal backup.
    'backups.backupBlockedByRestore':
      'Wait for the restore in progress to finish before starting a backup.',
    'backups.backupNotConfigured':
      'No scheduled backup is configured for this environment.',
    'backups.backupForbidden':
      'You are not allowed to start a backup for this environment.',
    'backups.backupFailedToStart': 'The backup could not be started.',
    // The server-side mutual exclusion, answered when the card's view was
    // stale: a refusal, worded apart from the tooltips that say "wait".
    'backups.backupRefusedRestoreRunning':
      'A restore is in progress: the backup was not started.',
    'backups.restoreRefusedBackupRunning':
      'A backup is in progress: the restore was not started.',
    'backups.restoreBlockedByBackup':
      'Wait for the backup in progress to finish before restoring.',
    // The download of one backup file, from the menu of its row. The button
    // shows the verb alone; its accessible name names the set. Each item says
    // what the file is and its size, the file name under it.
    'backups.downloadMenu': 'Download a file of backup {{id}}',
    'backups.downloadTooltip': 'Download a file',
    'backups.notVisible':
      'The backups of this environment are reserved to the editors of the project and the administrators.',
    'backups.pageRange': '{{from}}–{{to}} of {{total}}',
    'backups.previousPage': 'Previous backups',
    'backups.nextPage': 'Next backups',
    'backups.fileDatabase': 'Database',
    'backups.fileDatabaseNumbered': 'Database {{number}}',
    'backups.fileVolume': 'Volume {{number}}',
    'backups.fileWithSize': '{{label}} · {{size}}',
    'backups.downloadFailed': 'The download could not be prepared.',
    'backups.downloadForbidden':
      'You are not allowed to download a backup of this environment.',
    'backups.downloadMissing':
      'This file is no longer in the bucket. Start a new listing.',
    // The list read from the bucket by the backend itself, which is what the
    // card shows whenever it can — no workflow, so none of the "listing"
    // vocabulary above. The button re-reads the bucket: a display refresh,
    // hence "Refresh", the very word `backups.rerun` keeps away from the
    // listing. It shows `backups.refreshShort`; `backups.refreshList` is its
    // accessible name and tooltip.
    'backups.refreshShort': 'Refresh',
    'backups.refreshList': 'Refresh the list of backups',
    'backups.listedAt': 'List read at {{time}}',
    'backups.truncated':
      'The bucket holds more files than can be read at once: only part of the backups is listed.',
    // `backups.downloadMissing` for a list read from the bucket: the way to
    // update it is the Refresh button, not a listing.
    'backups.downloadMissingRefresh':
      'This file is no longer in the bucket. Refresh the list.',
    // The upload panel. "Send" throughout for the act, never "copy": the copy
    // is what the workflow does inside the cluster, and the user's act is
    // handing a file over.
    'upload.title': 'Send a file',
    // "pod" and not "container", because a pod is what the picker below offers.
    // The file does land in a container — the workflow's `containerName` is left
    // empty so it takes the first one — but naming that here would describe a
    // choice this card never lets anyone make.
    'upload.subtitle': 'Copy a file into a running pod of this environment',
    'upload.notConfigured': 'No upload is configured for this environment.',
    // The type's descriptor turns the feature off for this environment
    // (`{ id: uploads, when, reason }`): `reason`, the descriptor's French.
    'upload.featureOff': 'File upload unavailable: {{reason}}',
    'upload.featureOffGeneric':
      'File upload unavailable for this environment (set by its application type).',
    'upload.podLabel': 'Destination pod',
    'upload.noPods': 'No running pod to send a file to.',
    'upload.fileLabel': 'File',
    'upload.targetPathLabel': 'Destination directory',
    'upload.targetPathInvalid':
      'The destination must be an absolute directory, with no `..` segment and no shell character.',
    'upload.submit': 'Send',
    // The label the button takes while a send is in flight, next to BUI's own
    // spinner. It replaces the separate "in progress" line that used to sit
    // beside the button, so there is one indicator and it is on the control
    // that was pressed.
    'upload.submitting': 'Sending…',
    'upload.lastRun': 'Last send: {{date}}',
    // The history register: the run on screen is the last one the ENVIRONMENT
    // saw, not one this session started — days old, or a colleague's. Same
    // three cases as the confirmation sentences below (generic, detailed,
    // failed), but told as a dated fact and in one line, the date being part of
    // the sentence instead of a separate `upload.lastRun` line under it.
    'upload.lastRunHistory': 'Last send: {{date}}.',
    'upload.lastRunHistoryDetails':
      'Last send: {{date}} — {{file}} copied into {{path}} on pod {{pod}}.',
    'upload.lastRunHistoryFailed': 'Last send: {{date}} — it failed.',
    'upload.noRun': 'No file has been sent to this environment yet.',
    // The fallback wording, for a run that recorded nothing about what it
    // copied — one submitted from the Argo UI, typically.
    'upload.runSucceeded': 'The last file was copied into the pod.',
    // What is worth saying when the run did record it: which file went where.
    // `path` is the destination DIRECTORY and `file` the name the copy took in
    // it, which is the name that was picked in the form.
    'upload.runSucceededDetails':
      'The file {{file}} was copied into {{path}} on pod {{pod}}.',
    'upload.runFailed': 'The last send failed.',
    'upload.alreadyRunning': 'A send is already running for this environment.',
    // The tooltip of the disabled button, and the answer to a 403 that arrived
    // anyway. Two keys and not one, for the reason the backups pair states.
    'upload.submitNotAllowed':
      'Sending a file is reserved to administrators, and to the project’s editors on dev and its branches.',
    'upload.submitForbidden':
      'You are not allowed to send a file to this environment.',
    'upload.submitFailed': 'The file could not be sent.',
    'upload.tooLarge': 'This file is too large: the limit is 2 GiB.',
    // `reason` is the backend's own code — invalidPodName, invalidTargetPath,
    // fieldOrder, noFile — kept verbatim so a refusal can be traced back to the
    // check that made it. All four mean the form did not match its own contract,
    // which is a bug on our side rather than something the user can act on, so a
    // sentence per code would say no more than the code does.
    'upload.rejected': 'This send was refused by the server ({{reason}}).',
    // The fifth code, `noSuchPod`, gets a sentence of its own because it is the
    // only one a user can actually meet and act on: the pod list comes from the
    // page's resource-tree read, and a redeploy while the page is open renames
    // every pod. "Refresh" is the word on the page's own button, not a synonym.
    'upload.rejectedNoSuchPod':
      'This pod no longer exists. Refresh the page to see the current pods of this environment.',
    // The label of the disclosure the log sits behind. Collapsed by default —
    // it is a developer's tool, not the result of the send — and open from the
    // start when the run failed.
    'upload.logToggle': 'Execution log',
    'upload.logPruned':
      'The log of this send is gone — Argo Workflows only keeps its last twenty runs.',
    'upload.logUnreadable': 'The log of this send could not be read.',
    'upload.openInArgo': 'Open this run in Argo Workflows',
    'upload.openNamespaceInArgo':
      'Open the runs of this environment in Argo Workflows',
    'upload.retry': 'Try again',
    'upload.unavailable': 'The sends cannot be read right now.',
    // The sidebar block listing the Odoo projects. Short by necessity: the
    // sidebar label is clipped at a fixed width, so anything longer than two
    // words is read as an ellipsis.
    'nav.projects': 'Projects',
    'nav.noBranches': 'No environment',
    'nav.loadingBranches': 'Loading…',
    // The accessible name of a branch row, whose visible text is the name
    // alone: the description is the « Being deleted » state.
    'nav.branchLabel': '{{branch}} ({{description}})',
    // An ephemeral branch being deleted, which the sidebar shows without its
    // badge: « Being deleted » does not say the kind, so it is said here.
    // Otherwise `nav.branchLabel` does, its description being « Ephemeral
    // environment, … ».
    'nav.ephemeralBranchLabel':
      '{{branch}}, ephemeral branch ({{description}})',
    // The label above the ephemeral branches of the open project: how many
    // in git out of the per-project limit (`ephemeralCount` and `max` of the
    // branch meta route, the figures of the creation dialog), or the
    // environments listed alone when that route gives no count.
    'nav.ephemeralGroup': 'Ephemeral · {{used}}/{{max}}',
    'nav.ephemeralGroupNoMax': 'Ephemeral · {{used}}',
    // The toggle under the first five ephemeral branches, both ways; each
    // visible label is followed by its `…Context`, visually hidden, so the
    // button's accessible name reads as a sentence.
    'nav.moreEphemeralOne': '+ 1 more',
    'nav.moreEphemeralOneContext': 'ephemeral branch',
    'nav.moreEphemeral': '+ {{hidden}} more',
    'nav.moreEphemeralContext': 'ephemeral branches',
    'nav.lessEphemeral': 'Show less',
    'nav.lessEphemeralContext': 'of the ephemeral branches',
    // The compact menu (components/projectsNav/CompactProjectsNav.tsx): one badge per
    // project, its initials and a dot. The badge's accessible name is the
    // project, then its state in words (the dot alone would be colour alone);
    // the state is that of the home page's « To watch » (compactProjects.ts).
    'nav.projectBadge': '{{project}}, {{state}}',
    'nav.projectState.success': 'healthy',
    'nav.projectState.info': 'deploying',
    'nav.projectState.warning': 'to watch',
    'nav.projectState.danger': 'degraded',
    // No Application at all: asleep, or never deployed — the rail cannot
    // tell which without a read per project.
    'nav.projectState.none': 'no running environment',
    // The last entry of the menu a badge opens.
    'nav.openProject': 'Open the project',
    // The resources: « Ressources » card on the branch page's overview tab,
    // CPU and memory columns of the environments card. Figures from the
    // `waadoo-platform` backend — what the pods use now (metrics-server)
    // against their requests and limits, and the size allocated to the
    // volumes, not the space used on them.
    'resources.title': 'Resources',
    'resources.subtitle':
      'Current consumption, against the requests and limits of the pods',
    'resources.cpu': 'CPU',
    'resources.memory': 'Memory',
    'resources.cpuOf': 'CPU of {{branch}}',
    'resources.memoryOf': 'Memory of {{branch}}',
    // The accessible name of a CPU or memory gauge of the environments card
    // (`label` is `resources.cpuOf` or `resources.memoryOf`): the figure is in
    // it, the detail in its tooltip.
    'resources.gaugeLabel': '{{label}}: {{value}} ({{percent}})',
    'resources.gaugeLabelNoBar': '{{label}}: {{value}}',
    // `used` against `limit`, both in `unit`.
    'resources.value': '{{used}} / {{limit}} {{unit}}',
    'resources.quantity': '{{value}} {{unit}}',
    'resources.usageUnavailable': 'Consumption unavailable',
    'resources.details': 'Requested: {{requested}} · Limit: {{limit}}',
    'resources.requestedMarker': 'Requested: {{requested}}',
    // The limit of a namespace where some container has none.
    'resources.unbounded': 'unlimited',
    // Invariant, the same word in both languages and on the home page's
    // « Platform » card (`platform.cpuFigures`).
    'resources.unitCores': 'vCPU',
    'resources.unitMiB': 'MiB',
    'resources.unitGiB': 'GiB',
    'resources.volumes': 'Volumes',
    'resources.volumesNote': 'Allocated size, not the space used',
    'resources.volumesNone': 'No volume',
    'resources.volumesTotal': 'Volumes: {{size}}',
    'resources.unavailableShort': 'unavailable',
    'resources.volumeAttachments': 'Attachments',
    'resources.volumeDatabase': 'Database',
    'resources.sizeUnknown': 'size unknown',
    'resources.grafana': 'See detailed graphs in Grafana',
    'resources.unavailable': 'The resources cannot be read right now.',
    'resources.environmentUnavailable': 'Resources unavailable',
    // The reason of a CPU or memory cell whose environment is past the usage
    // route's cap (`truncated` answer, see `resources.truncated`).
    'resources.beyondCap':
      'Not read: beyond the first {{shown}} environments of the project',
    // The line under the environments table: `label` is `resources.total`,
    // or `resources.partial` when some environment could not be read.
    'resources.total': 'Project total',
    'resources.partial': 'Total of the readable environments only',
    'resources.totalLine':
      '{{label}}: CPU {{cpu}} · Memory {{memory}} · Volumes {{volumes}}',
    'resources.truncated':
      'Consumption of the first {{shown}} environments out of {{total}}.',
    // « Projet », the card on top of an Odoo project's overview
    // (ProjectCard.tsx), in place of the catalog's About and Links cards.
    // The two action names say « metadata » where the catalog's About card
    // says only « Refresh » / « Edit »: here they sit in a card titled with
    // the project's name, where a bare verb would read as acting on the
    // project itself.
    'project.refresh': 'Refresh metadata',
    'project.refreshScheduled': 'Refresh scheduled',
    'project.edit': 'Edit metadata',
    'project.noDescription': 'No description',
    'project.createdBy': 'Created by',
    'project.createdOn': 'Created on {{date}}',
    // The project page card's creation pills (ProjectCreationPills): the
    // time as shown (`hour` without a leading zero, `dayPeriod` AM/PM), then
    // the visually hidden words that make the line read « Created by Dev 1,
    // on 10/05/2026 at 9:42 AM » — `createdOnLead` replaces `createdOnJoin`
    // without a creator.
    'project.createdTime': '{{hour}}:{{minute}} {{dayPeriod}}',
    'project.createdOnJoin': ', on',
    'project.createdOnLead': 'Created on',
    'project.createdAtJoin': 'at',
    // The accessible name of the project page's tab list (ProjectHeader).
    'project.tabsLabel': 'Sections of this project',
    'project.links': 'Links',
    'project.tags': 'Tags',
    'project.newTab': '(new tab)',
    // The « Projet » card's « Pipelines » tile while a pipeline runs
    // (ProjectCard.tsx): its subtitle, then what its accessible name
    // adds after « (new tab) » — that name is an aria-label, which the
    // subtitle under it is not part of.
    'project.pipelineRunning': 'Running · {{branch}}',
    'project.pipelinesRunning_one': 'Running · {{count}} branch',
    'project.pipelinesRunning_other': 'Running · {{count}} branches',
    'project.pipelineRunningOn': '— pipeline running on {{branch}}',
    'project.pipelinesRunningOn_one': '— pipeline running on {{count}} branch',
    'project.pipelinesRunningOn_other':
      '— pipelines running on {{count}} branches',
    // Creating and deleting an ephemeral branch (NewBranchDialog.tsx,
    // DeleteBranchDialog.tsx) and the « being prepared » state of a branch
    // page whose environment does not exist yet (BranchPreparing.tsx).
    // The card's « create » button (CreateButton): its title, then its
    // subtitle, `source` being the dialog's default starting branch; its
    // accessible name is both. Then the dashed row under the table: the
    // dialog's title, then this line, the quota in place of it when reached.
    'newBranch.open': 'New branch',
    'newBranch.openSubtitle': 'ephemeral, from {{source}}',
    'newBranch.slotText': 'created from {{source}} · {{used}}/{{max}} used',
    'newBranch.quotaReached': 'Ephemeral branch quota reached',
    'newBranch.quotaReachedCount':
      'Ephemeral branch quota reached ({{used}}/{{max}})',
    'newBranch.menuItem': '+ New ephemeral branch',
    'newBranch.title': 'New ephemeral branch',
    // The line under the title: the project, the quota gauge (decorative),
    // then these words, which carry the quota.
    'newBranch.quota': '{{used}}/{{max}} used',
    'newBranch.limitReached':
      'Limit of {{max}} ephemeral branches reached: delete one before creating another.',
    'newBranch.nameLabel': 'Branch name',
    // The prefix is drawn inside the field and hidden from screen readers;
    // this sentence, part of the field's description, says it instead.
    'newBranch.prefixHint': 'Prefix {{prefix}} imposed.',
    'newBranch.nameHelp': 'Lowercase letters, digits and hyphens.',
    // Characters left before the length limit, at the end of the field.
    'newBranch.remaining_one': '{{count}} left',
    'newBranch.remaining_other': '{{count}} left',
    'newBranch.problemChars': 'Only lowercase letters, digits and hyphens.',
    'newBranch.problemHyphen':
      'No hyphen at the start or the end, and no two hyphens in a row.',
    'newBranch.problemTooLong':
      'Too long: {{max}} characters at most, prefix included.',
    'newBranch.sourceLabel': 'Start from branch',
    // The description of the default source in the list of sources.
    'newBranch.defaultSource': 'current code and image',
    // The « what will be created » panel: the git branch, the environment
    // (its future address, or the branch page without one), the delay.
    'newBranch.planTitle': 'What will be created',
    'newBranch.planBranch': 'Git branch',
    'newBranch.planFrom': 'from',
    'newBranch.planEnvironment': '{{app}} environment',
    'newBranch.planDelay': 'Ready in 4 to 6 min',
    'newBranch.planDelayDetail':
      'image build and ArgoCD detection, then deployment — followed live on the branch page',
    'newBranch.errorExists': 'The branch {{branch}} already exists.',
    'newBranch.errorLimit': 'Limit of {{max}} ephemeral branches reached.',
    'newBranch.errorSource': 'The source branch no longer exists.',
    'newBranch.errorName': 'Name refused by the server.',
    'newBranch.errorRefused':
      'Creation refused: insufficient rights, or project not found.',
    'newBranch.errorUnknown': 'The creation failed. Try again in a moment.',
    'newBranch.cancel': 'Cancel',
    'newBranch.submit': 'Create the branch',
    'deleteBranch.open': 'Delete the branch',
    'deleteBranch.title': 'Delete the branch {{branch}}',
    'deleteBranch.body':
      'This deletes the git branch {{branch}} and, within a few minutes, its environment: the {{app}} application, its database and its attachments.',
    'deleteBranch.commits':
      'Commits merged into no other branch will no longer be reachable.',
    'deleteBranch.noBackup':
      'An ephemeral environment has no backup: nothing can be restored from Backstage.',
    'deleteBranch.confirmLabel': 'To confirm, type the branch name: {{branch}}',
    'deleteBranch.errorProtected':
      'This branch is protected in GitLab: it cannot be deleted from here.',
    'deleteBranch.errorNotFound': 'Branch not found, or insufficient rights.',
    'deleteBranch.errorUnknown': 'The deletion failed. Try again in a moment.',
    'deleteBranch.cancel': 'Cancel',
    'deleteBranch.submit': 'Delete the branch',
    'deleteBranch.inProgress': 'Deleting {{branch}} (a few minutes).',
    // An ephemeral environment being deleted (branchDeletion.ts): the badge
    // of every branch list, the branch page's banner and the line replacing
    // its data actions, and the notice once the environment is gone.
    'deletion.badge': 'Being deleted',
    'deletion.banner':
      'This branch is being deleted. The environment will disappear in a few minutes.',
    'deletion.dataUnavailable':
      'Backups and imports are unavailable while the environment is being deleted.',
    'deletion.done': '{{branch}} deleted',
    // The « mise en service » of an ephemeral branch (BranchService.tsx,
    // BranchPreparing.tsx, BranchReady.tsx): its steps' names and states.
    'preparing.stepBranch': 'Branch created',
    'preparing.stepDetection': 'Detection by ArgoCD',
    'preparing.stepDeployment': 'Deployment',
    'preparing.stepReady': '{{app}} ready',
    'preparing.statusDone': 'Done',
    'preparing.statusRunning': 'In progress',
    'preparing.statusFailed': 'Failed',
    'preparing.statusWaiting': 'Waiting',
    // The second line of each step. The build's: the run and how long it
    // has run, each left out when unknown, then the link.
    'preparing.pipelineRun': '{{forge}} pipeline #{{id}}',
    'preparing.pipelineFor': 'for {{elapsed}}',
    'preparing.pipelineOpen': 'View the pipeline',
    'preparing.pipelineLink': 'Pipelines of the branch',
    'preparing.detectionDelay': 'about 1 min',
    'preparing.deploymentDetail': 'database and {{app}}',
    'preparing.polling':
      'This page checks every 10 seconds and shows the environment as soon as it appears.',
    'preparing.timedOut':
      'Still no environment after 15 minutes. Check the branch’s pipeline and that the branch contains deploy/Chart.yaml, then refresh the page.',
    // « Mise en service » (BranchService.tsx): the card, its milestones and
    // their estimates — those of the validated mockup, not measured.
    'service.title': 'Going live',
    'service.following': 'Automatic follow-up · 10 s',
    'service.image': '{{app}} image',
    'service.imageRun': 'pipeline #{{id}}',
    'service.imageEstimate': '≈ 2 to 4 min',
    'service.environment': 'Environment',
    'service.environmentSteps': 'detection, deployment, {{app}} ready',
    'service.deploying': 'Deployment in progress',
    'service.environmentEstimate': '≈ 2 to 3 min after the image',
    'service.hintPreparing':
      'The branch page shows as soon as the environment appears, and follows its deployment until {{app}} is ready.',
    'service.hintDeploying':
      'This card follows the deployment until {{app}} is ready.',
    // The timeline: its card, its phases, the steps' words and pills.
    'service.detailTitle': 'Detail',
    'service.phaseBranch': 'Branch',
    'service.phaseBuild': 'Build',
    'service.stepGit': 'Git branch',
    'service.stepDetection': 'ArgoCD detection',
    'service.buildWaiting': 'no pipeline visible yet',
    'service.readyDetail': 'pods ready, application healthy',
    'service.pillDone': 'Done · {{duration}}',
    'service.pillRunning': 'In progress · {{duration}}',
    // « Ce qui va être créé » / « Ce qui a été créé ».
    'service.plannedTitle': 'What will be created',
    'service.createdTitle': 'What was created',
    'service.planned': 'Planned',
    'service.created': 'Created',
    'service.count': '{{items}} item(s)',
    'service.imageByCi': 'built by the CI',
    'service.rowDatabase': 'PostgreSQL database',
    'service.databaseFresh': 'new database',
    'service.rowMails': 'Emails',
    'service.mailsCaptured': 'captured',
    'service.mailsTab': 'Emails tab',
    'service.rowAddress': 'Address',
    // A duration, as short as a pill allows (`useDurationWords`).
    'duration.underSecond': '< 1 s',
    'duration.seconds': '{{seconds}} s',
    'duration.minutes': '{{minutes}} min {{seconds}} s',
    'duration.hours': '{{hours}} h {{minutes}} min',
    // The « ready » banner (BranchReady.tsx). `branch` is an element (bold).
    'ready.title': 'Environment {{branch}} ready',
    'ready.titleIn': 'Environment {{branch}} ready in {{duration}}',
    'ready.openApp': 'Open {{app}}',
    'ready.showReport': 'View the report',
    'ready.dismiss': 'Close the banner',
    // The report it opens. Every `lead`, `address` is an element.
    'report.title': 'Report — {{branch}}',
    'report.pipeline': 'Pipeline #{{id}}',
    'report.steps': 'Steps',
    'report.next': 'What next?',
    'report.nextPushLead': 'Push to {{branch}}',
    'report.nextPush':
      '{{lead}}: every push rebuilds the image and redeploys the environment (≈ 3 min).',
    'report.nextTestLead': 'Test',
    'report.nextTest': '{{lead}} on {{address}}.',
    'report.nextTestMails':
      '{{lead}} on {{address}}; the emails sent are in the « Emails » tab.',
    'report.nextTestPlain': '{{lead}} your changes in the environment.',
    'report.nextMergeLead': 'Merge into {{target}}',
    'report.nextMergeOriginLead': 'Merge into the branch of origin',
    'report.nextMerge':
      '{{lead}} through a merge request, then delete the branch to free its slot.',
    'report.nextMergeQuota':
      '{{lead}} through a merge request, then delete the branch to free its slot ({{used}}/{{max}} used).',
    'copy.label': 'Copy the commands',
    'copy.done': 'Copied',
    'copy.failed': 'Copy unavailable: select the commands by hand',
    'error.notJson':
      'Unexpected response from the ArgoCD API (not JSON): {{body}}',
    'error.noIdentity':
      'Not signed in to Authelia. Guest sign-in cannot query ArgoCD.',
    // The « Lecture seule » badge (ReadOnlyBadge.tsx): a project the user is
    // a member of without being an editor. `label` is its visible text and
    // accessible name, `tooltip` what it means, `headerLabel` its label in the
    // metadata row of the project and branch headers, `navProject` the
    // accessible name of a project row of the sidebar carrying it.
    'readOnly.label': 'Read-only',
    'readOnly.tooltip':
      'Read-only: you are a member of this project, without editor rights (no terminal, branch creation or deletion, deployment restart or restore).',
    'readOnly.headerLabel': 'Access',
    // The project header's version of its application, for a type whose
    // module shows none (`appVersion.tsx`): the prefix of the dev
    // environment's image tag, « Wagtail 7.4 ».
    'appVersion.headerLabel': 'Version',
    'appVersion.label': '{{app}} {{version}}',
    'readOnly.navProject': '{{project}}, read-only',
    // The « À surveiller » card of the overview (WarningEventsCard.tsx): the
    // recent Kubernetes Warning events of the environment, one row per object
    // and reason. `times` and not `count`: no plural form is wanted — the app
    // language stays `en` under the French overrides (see `branch.ephemeral`).
    'events.title': 'Needs a look',
    'events.subtitle': 'Kubernetes warnings of the last 6 hours',
    'events.listLabel': 'Recent Kubernetes warnings',
    // The card is folded by default, down to its header: the level chip —
    // `serious` when one warning is (isSeriousWarning, warningEvents.ts),
    // `transient` otherwise — and the summary. `age` in kubectl's compact
    // notation; `label` the latest warning's chip words, `object` its object.
    'events.level.serious': 'Action needed',
    'events.level.transient': 'Transient',
    'events.collapsedSummary_one':
      '{{count}} warning · {{age}} ago: {{label}} · {{object}}',
    'events.collapsedSummary_other':
      '{{count}} warnings · latest {{age}} ago: {{label}} · {{object}}',
    'events.category.oom': 'Out of memory',
    'events.category.crashLoop': 'Restarting in a loop',
    'events.category.probe': 'Probe failing',
    'events.category.image': 'Image not found',
    'events.category.scheduling': 'Waiting to be scheduled',
    'events.category.volume': 'Volume',
    'events.category.job': 'Job failed',
    'events.category.evicted': 'Pod evicted',
    // An entry about a platform resource (Crossplane, provider-ovh), shown to
    // admins only, by what the provider failed to do and why; `ovhApi`: OVH's
    // API answered 5xx/429 for a moment. `generic`: any other platform reason.
    'events.platform.observe.ovhApi':
      'Check postponed — OVH API briefly unavailable',
    'events.platform.observe.other': 'Check failed',
    'events.platform.create.ovhApi':
      'Creation failed — OVH API briefly unavailable',
    'events.platform.create.other': 'Creation failed',
    'events.platform.update.ovhApi':
      'Update failed — OVH API briefly unavailable',
    'events.platform.update.other': 'Update failed',
    'events.platform.delete.ovhApi':
      'Deletion failed — OVH API briefly unavailable',
    'events.platform.delete.other': 'Deletion failed',
    'events.platform.generic': 'Platform',
    // A reason without a category of its own: Kubernetes' word for it.
    'events.categoryOther': '{{reason}}',
    // « ×4 » on screen; the words for a screen reader, singular apart (no
    // plural forms, see above).
    'events.count': '×{{times}}',
    'events.countLabel': '{{times}} occurrences',
    'events.countLabelOne': '1 occurrence',
    // `age`: kubectl's compact notation (compactDuration), « 12m30s ».
    'events.lastSeen': '{{age}} ago',
    'events.seenTitle': 'Last seen {{last}} · first seen {{first}}',
    'events.reasonTitle': 'Kubernetes reason: {{reason}}',
    // The code line under the summary band (BranchCodeLine.tsx), read from
    // GitLab: the branch's last commit and, for an ephemeral branch, how far
    // it is from dev and its merge request. `n` is a number or « 100+ ».
    'code.label': 'Code of the branch',
    'code.lastCommit': 'Last commit',
    'code.committedAgo': '{{age}} ago',
    'code.openCommit': 'Open commit {{sha}} in GitLab',
    'code.commitTitle': 'Committed {{date}}',
    'code.ahead': '{{n}} ahead',
    'code.behind': '{{n}} behind {{base}}',
    'code.divergenceTitle': 'Commits compared with {{base}}',
    'code.mrOpened': 'MR !{{iid}} open',
    'code.mrMerged': 'MR !{{iid}} merged',
    'code.mrDraft': 'draft',
    'code.openMr': 'Open merge request !{{iid}} in GitLab: {{title}}',
    'code.mrPipeline': 'Pipeline of the merge request',
    'code.noMr': 'No merge request',
    'code.createMr': 'Create a merge request',
    'initReport.jobLog': 'View the job log',
    // The failing-deployment banner of the overview (SyncFailureBanner.tsx).
    // `commit` is an element (the commit's link). The `_one`/`_other` pair
    // is only used with a count of 1 or more, where English and French agree
    // (see `backups.contentDatabases`).
    'syncFailure.retrying':
      'The deployment is failing: retry in progress ({{progress}}) on commit {{commit}}. A fixing commit is picked up at the next attempt.',
    'syncFailure.failed_one':
      'The deployment of commit {{commit}} failed after {{count}} retry.',
    'syncFailure.failed_other':
      'The deployment of commit {{commit}} failed after {{count}} retries.',
    'syncFailure.failedOnce': 'The deployment of commit {{commit}} failed.',
    'syncFailure.unknownCommit': 'unknown',
    'syncFailure.resource': '{{kind}} {{name}}: {{message}}',
    'syncFailure.resourceNoMessage': '{{kind}} {{name}} failed.',
    'syncFailure.restart': 'Retry on the latest commit',
    'syncFailure.restarted': 'Deployment restarted on the latest commit.',
    'syncFailure.error.forbidden':
      'You are not allowed to restart the deployment of this environment.',
    'syncFailure.error.notFound':
      'The ArgoCD application of this environment cannot be found.',
    'syncFailure.error.busy':
      'A synchronisation is still in progress. Try again in a moment.',
    'syncFailure.error.failed':
      'The deployment could not be restarted. Try again in a moment.',
    // The « Promotion » card of a project page and its dialog
    // (`promotion.ts`, `PromotionCard.tsx`, `PromoteDialog.tsx`; mockups
    // promo-D, 2026-10-15, and R3-A for the card, 2026-10-19).
    'promotion.title': 'Promotion',
    'promotion.subtitle': '{{chain}}, by merge request',
    'promotion.chainLabel': 'Promotion chain',
    'promotion.gap_one': '+{{count}} commit',
    'promotion.gap_other': '+{{count}} commits',
    'promotion.gapNone': 'up to date',
    'promotion.deployed': 'Deployed',
    'promotion.neverDeployed': 'never deployed',
    'promotion.noBranch': 'branch missing from the repository',
    'promotion.deployedAgo': 'deployed {{ago}}',
    'promotion.nextTitle': 'Next step: promote {{source}} to {{target}}',
    'promotion.nextCommits_one':
      '{{count}} commit tested on {{source}}, image {{image}}.',
    'promotion.nextCommits_other':
      '{{count}} commits tested on {{source}}, image {{image}}.',
    'promotion.nextCommitsNoImage_one':
      '{{count}} commit tested on {{source}}.',
    'promotion.nextCommitsNoImage_other':
      '{{count}} commits tested on {{source}}.',
    'promotion.nextAsleep':
      '{{target}} is asleep: the version will be deployed there at its next wake.',
    'promotion.nextAdmins': 'This promotion is made by the administrators.',
    'promotion.upToDateTitle': 'Everything is up to date',
    'promotion.upToDateDetail':
      'Every environment of the chain has the commits of the previous one.',
    'promotion.openMergeRequest': 'Merge request already open',
    'promotion.openMergeRequestPair':
      'Merge request {{source}} → {{target}} already open',
    'promotion.commitsTitle': 'Commits to promote · {{total}}',
    'promotion.showAll_one': 'Show the {{count}} commit',
    'promotion.showAll_other': 'Show the {{count}} commits',
    'promotion.showFewer': 'Show fewer',
    'promotion.moreCommits_one': 'and {{count}} more',
    'promotion.moreCommits_other': 'and {{count}} more',
    'promotion.image': 'Image',
    'promotion.imageBuilt':
      'built and tested on {{source}} · taken up as is by {{target}}, without a rebuild',
    'promotion.imageReplaces': 'replaces {{image}}',
    'promotion.sourceNoImage': '{{source}} has no image yet',
    'promotion.targetNoImage': '{{target}} has no image yet',
    'promotion.ciUnchanged': 'CI unchanged',
    'promotion.otherActions': 'Other actions:',
    'promotion.ruleAdmins': '{{target}}: promoted by the administrators',
    'promotion.button': 'Promote {{source}} → {{target}}',
    'promotion.branchButton': 'Promote to {{target}}',
    'promotion.ciLocked': 'Changes the CI: reserved for administrators',
    'promotion.oldCiLocked':
      'The project’s CI still builds on this branch: reserved for administrators (migrate the CI)',
    'promotion.footerReadOnly':
      'Promotions are made by the project’s editors and the administrators.',
    // The folded card (2026-10-29): the next step's pill and the chevron.
    'promotion.pillPromote_one': '{{count}} commit to promote to {{target}}',
    'promotion.pillPromote_other': '{{count}} commits to promote to {{target}}',
    'promotion.pillAdmins_one':
      '{{count}} commit to promote to {{target}} by the administrators',
    'promotion.pillAdmins_other':
      '{{count}} commits to promote to {{target}} by the administrators',
    'promotion.pillUpToDate': 'All branches are up to date',
    'promotion.showDetails': 'Show the details',
    'promotion.hideDetails': 'Hide the details',
    'promote.title': 'Promote {{source}} → {{target}}',
    'promote.subtitleEditors': '{{project}} · editors and administrators',
    'promote.subtitleAdmins': '{{project}} · administrators',
    'promote.commitsTitle_one': '{{count}} commit arrives on {{target}}',
    'promote.commitsTitle_other': '{{count}} commits arrive on {{target}}',
    'promote.moreCommits': '… and {{more}} more',
    'promote.changesTitle': 'What changes on {{target}}',
    'promote.changeCode': 'the code and the modules of these commits',
    // A `standard` project (point 8): no code nor image of its own.
    'promote.changeCodeStandard':
      'the version and the settings of these commits',
    'promote.changeImage':
      'the image: {{source}}’s, already built and tested, taken up as is ({{target}} never builds)',
    'promote.changeCommon':
      'the common settings (deploy/values.yaml) if they changed',
    'promote.keptTitle': 'What does not change',
    // Generic over the type (genericity plan, step 6): a type's module may
    // say it in its own words (Odoo's: plugins/odoo, `promote.kept*`).
    'promote.keptCapacity':
      'capacity: {{component}} {{cpu}} CPU, {{memory}} memory',
    'promote.keptHpa': 'HPA {{min}} → {{max}} pods',
    'promote.keptComponentHpa': '{{component}}: HPA {{min}} → {{max}} pods',
    'promote.keptPods_one': '{{component}}: {{count}} pod (no autoscaling)',
    'promote.keptPods_other': '{{component}}: {{count}} pods (no autoscaling)',
    'promote.keptNoAutoscaling': '{{component}}: no autoscaling',
    'promote.keptDisabled': '{{component}}: disabled',
    'promote.keptEnabled': '{{component}}: enabled',
    'promote.keptVolumes': 'volumes: {{volumes}}',
    'promote.keptVolume': '{{label}} {{size}}',
    'promote.volumeFiles': 'files',
    'promote.volumeDatabase': 'database',
    'promote.keptDatabaseInstances': 'PostgreSQL: {{instances}} instance(s)',
    'promote.keptSetting': '{{label}}: {{value}}',
    'promote.valueYes': 'yes',
    'promote.valueNo': 'no',
    'promote.keptGeneric':
      'the configuration of {{target}}: capacity, volumes, high availability ({{target}}/values.yaml of the appenv)',
    'promote.keptData': 'the data of {{target}}: database and files',
    'promote.keptDataDatabase': 'the data of {{target}}: database',
    'promote.keptDataFiles': 'the data of {{target}}: files',
    'promote.keptDataGeneric': 'the data of {{target}}',
    'promote.how':
      'Backstage opens (or takes up) the merge request {{source}} → {{target}} and merges it; the CI of {{target}} then takes up the image already built and tested on {{source}}, and {{target}} is redeployed. Followed in « Tasks ».',
    'promote.howSleeping':
      'Backstage opens (or takes up) the merge request {{source}} → {{target}} and merges it; the CI of {{target}} then takes up the image already built and tested on {{source}}. {{target}} is asleep: it will be deployed at its next wake. Followed in « Tasks ».',
    // A `standard` project (point 8): its source deployed at its head, no
    // image to take up (backend `promote.ts`).
    'promote.howStandard':
      'Backstage checks that {{source}} is deployed at its head, opens (or takes up) the merge request {{source}} → {{target}} and merges it; {{target}} is then redeployed at that revision, with no image to build nor to take up. Followed in « Tasks ».',
    'promote.howSleepingStandard':
      'Backstage checks that {{source}} is deployed at its head, opens (or takes up) the merge request {{source}} → {{target}} and merges it, with no image to build nor to take up. {{target}} is asleep: it will be deployed at its next wake. Followed in « Tasks ».',
    'promote.ciWarning':
      'This promotion changes the CI ({{files}}): review these files before promoting.',
    'promote.oldCiWarning':
      'The CI of {{target}} still builds ({{jobs}}): the promoted code will run with Backstage’s token. Migrate the project’s CI.',
    'promote.viewMergeRequest': 'View the merge request',
    'promote.submit': 'Promote',
    // The « Configuration » tab (`configurationForm/`, `configuration.ts`;
    // mockup « D », 2026-10-16): an environment's capacity, volumes and high
    // availability, as its values.yaml holds them. The words of a type's own
    // fields and sections are its module's (Odoo's: plugins/odoo, since step
    // 4 of the genericity plan).
    'configuration.unavailable':
      'The configuration of this environment is not available here.',
    'configuration.failed': 'The configuration could not be read.',
    'configuration.unreadableTitle': 'Some values of {{files}} cannot be read',
    'configuration.unreadableDescription':
      'Nothing can be changed from here until an administrator fixes the file.',
    'configuration.lockReadOnly':
      'Read-only: the project’s editors and the administrators change this configuration.',
    'configuration.lockProduction':
      'The configuration of prod is changed by the administrators.',
    'configuration.sleeping':
      '{{branch}} is asleep: a change is committed now and applied at its next wake.',
    'configuration.tierEditors': 'editors',
    'configuration.tierAdmins': 'administrators',
    'configuration.tierRead': 'read-only',
    'configuration.panelLabel': 'Quota, comparison and changes',
    'configuration.capacityTitle': 'Capacity',
    'configuration.differences_one': '{{count}} difference with {{branch}}',
    'configuration.differences_other': '{{count}} differences with {{branch}}',
    'configuration.noDifference': 'No difference with {{branch}}',
    'configuration.changesTitle': 'Changes · {{changes}}',
    // The list of the project's environments on the left (layout « C3 »):
    // each opens its own Configuration tab.
    'configuration.environments': 'Environments',
    'configuration.environmentsHint': 'Opens the configuration of that branch.',
    'configuration.environmentLocked': 'read-only for you',
    // « Comparer avec »: another environment's value, after each field.
    'configuration.compareWith': 'Compare with',
    'configuration.compareNone': 'none',
    'configuration.reference': '{{branch}}: {{value}}',
    // The gauges of the live ResourceQuota of the namespace.
    'configuration.quotaTitle': 'Environment quota',
    'configuration.quotaCpuShort': 'Reserved CPU',
    'configuration.quotaMemoryShort': 'Reserved memory',
    'configuration.quotaCpu': 'Reserved CPU quota',
    'configuration.quotaMemory': 'Reserved memory quota',
    'configuration.quotaPods': 'Pods',
    'configuration.quotaAsleep':
      'No live quota: {{branch}} is asleep and has no namespace.',
    'configuration.quotaMissing':
      'No quota read for this environment’s namespace.',
    'configuration.instances': 'Instances',
    'configuration.ha': 'High availability',
    'configuration.profile': 'Profile',
    'configuration.dbCpu': 'PostgreSQL — CPU reserved / ceiling',
    'configuration.dbMemory': 'PostgreSQL — memory reserved / ceiling',
    'configuration.perPod': 'per pod',
    'configuration.perInstance': 'per instance',
    'configuration.was': 'was {{value}}',
    'configuration.growOnly': 'grow only',
    'configuration.pods': 'Pods minimum / maximum',
    'configuration.instances1': '1',
    'configuration.instances2': '2 inst.',
    'configuration.instances3': '3 inst.',
    'configuration.modeSingle': 'Single instance',
    'configuration.modeHa2': 'HA — 2 instances, two zones (strict)',
    'configuration.modeHa3': 'HA — 3 instances, one per zone (strict)',
    'configuration.syncSingle': 'none with a single instance',
    'configuration.yes': 'yes',
    'configuration.no': 'no',
    'configuration.appliedAtWake_one': 'applied at the next wake',
    'configuration.appliedAtWake_other': 'applied at the next wake',
    'configuration.noChanges': 'No change yet.',
    'configuration.derived': 'derived',
    'configuration.peak': 'At peak: {{cpu}} · {{memory}} reserved',
    'configuration.margin': 'cluster headroom: {{cpu}} · {{memory}}',
    'configuration.note':
      'Commit in {{repo}} · {{file}}, then redeployment of {{branch}} ({{restart}}). The code is not touched.',
    'configuration.noteSleeping':
      'Commit in {{repo}} · {{file}}; {{branch}} is asleep and takes it at its next wake. The code is not touched.',
    'configuration.cancel': 'Cancel',
    'configuration.apply': 'Apply',
    'configuration.problemLocked':
      '{{field}}: the current value is beyond the creation form’s bounds, reserved for administrators.',
    'configuration.problemBounds':
      '{{field}}: from {{min}} to {{max}}, step {{step}}.',
    'configuration.problemBeyondEditors': 'Beyond: administrators.',
    'configuration.problemShrink':
      '{{field}}: a volume can only grow ({{current}} GiB now).',
    'configuration.problemRequestAboveLimit':
      '{{field}}: above its ceiling, Kubernetes would refuse the pod.',
    'configuration.problemMaxBelowMin':
      'The maximum of pods must be at least the minimum.',
    'configuration.problemSharedBuffers':
      'The reserved memory must cover a quarter of the ceiling (shared_buffers = {{buffers}} MiB), or CloudNativePG refuses the database.',
    // The generic tab (`configurationForm/GenericConfigurationTab.tsx`,
    // genericity plan step 5): the structure of every type without a tab of
    // its own; the fields' and components' names are the descriptor's.
    'configuration.capacityDescriptionGeneric':
      'Profile of each pod of the {{app}} application; the fine values below.',
    'configuration.lockEphemeralGeneric':
      'Effective configuration of this ephemeral branch: dev’s resources, lightened for ephemeral branches. Read-only.',
    'configuration.noteGeneric':
      'Commit in {{repo}} · {{file}}, then redeployment of {{branch}}. The code is not touched.',
    'configuration.componentCpu': '{{component}} — CPU reserved / ceiling',
    'configuration.componentMemory':
      '{{component}} — memory reserved / ceiling',
    'configuration.componentPods': '{{component}} — pods minimum / maximum',
    'configuration.podCount_one': '{{count}} pod',
    'configuration.podCount_other': '{{count}} pods',
    'configuration.podRange': '{{min}} → {{max}} pods',
    'configuration.componentDisabled': 'disabled',
    'configuration.groupTitle': '{{group}} ({{members}})',
    'configuration.groupComponent': 'Component',
    'configuration.groupPods': 'Pods',
    'configuration.groupCpu': 'CPU',
    'configuration.groupMemory': 'Memory',
    'configuration.volumesSection': 'Volumes',
    'configuration.usedBy': 'used by: {{components}}',
    'configuration.mariadbNote': 'One instance, without high availability.',
    'configuration.othersSection': 'Other settings',
    'configuration.adjusted': 'set by the type’s rules',
    'configuration.problemValues': '{{field}}: allowed values {{allowed}}.',
    // The access card (CredentialsCard.tsx), alone in the Access tab of a type
    // declaring the `credentials` feature: the administrator credentials of
    // the environment, read from the Kubernetes Secret its chart keeps them
    // in (moved from the Odoo module, genericity plan, step 7; `{{app}}` the
    // type's title). "Show" and not "Reveal": the same word the browser's
    // own password fields use, so the button needs no learning.
    'credentials.title': '{{app}} access',
    'credentials.subtitle': 'Administrator sign-in for this environment',
    'credentials.login': 'Login',
    'credentials.password': 'Password',
    'credentials.reveal': 'Show',
    'credentials.hide': 'Hide',
    'credentials.copy': 'Copy',
    'credentials.copied': 'Copied',
    // Names the Secret rather than saying "a Kubernetes Secret": where the
    // password lives is the point of the card, and the name is what makes it
    // checkable with kubectl by whoever wants to.
    'credentials.source':
      'Generated with the environment and kept in the Kubernetes Secret {{secret}}.',
    // A fact, not a failure — the register of `backups.notConfigured`: nothing
    // is broken, the environment simply has not initialised yet.
    'credentials.notConfigured':
      'No administrator credentials for this environment. They are generated on its first initialisation.',
    'credentials.unavailable': 'The credentials cannot be read right now.',
    'credentials.retry': 'Try again',
    // The clipboard is unavailable in a non-secure context and can be refused
    // outright; the sentence has to say what to do instead, or the button reads
    // as broken.
    'credentials.copyFailed':
      'The browser refused the copy: show the password and select it.',
    // Only for a type declaring `credentials.loginPath`: none for Odoo.
    'credentials.loginPage': 'Open the sign-in page',
    // In place of the credentials when the page knows the route would refuse
    // them (rule of 2026-10-29, `credentialsAccess`): a plain member of the
    // project, an editor on the production. The register of
    // `backups.notVisible`: who may, not an error.
    'credentials.editorsOnly':
      'Credentials reserved for the project’s editors.',
    'credentials.adminsOnly':
      'Production credentials reserved for the platform’s administrators.',
    // The point-in-time restore (plan Plateforme-backup-restore, step 8,
    // lot 3): its card on the Données tab (PitrCard.tsx), its day dialog
    // (PitrDayDialog.tsx), its confirmations (PitrDialogs.tsx), the « à
    // valider » banner and header badge. Every time is Paris time, said so.
    'pitr.title': 'Point-in-time restore',
    'pitr.subtitle': 'Database and media brought back together, to the second',
    'pitr.subtitleDatabaseOnly': 'Database brought back to the second',
    'pitr.unavailableRead': 'The point-in-time restore could not be read.',
    'pitr.retry': 'Retry',
    'pitr.archivingOk': 'Logs archived since {{since}}',
    'pitr.archivingOkNoDate': 'Logs archived',
    'pitr.archivingKo': 'Log archiving failing since {{since}}',
    'pitr.archivingKoNoDate': 'Log archiving failing',
    'pitr.windowLine':
      'Database restorable to the second from {{from}} until now (Paris time).',
    'pitr.shortcutsLabel': 'Go back…',
    'pitr.shortcutDeploy': 'Just before the last deployment · {{date}}',
    'pitr.shortcutConfig':
      'Just before the last configuration change · {{date}}',
    'pitr.shortcutHour': '1 hour ago',
    'pitr.shortcutOther': 'Another time…',
    'pitr.daysLabel':
      '…or pick a day (restorable from {{from}} until now, Paris time)',
    'pitr.daySnapshots_one': '{{count}} snapshot',
    'pitr.daySnapshots_other': '{{count}} snapshots',
    'pitr.dayBarman_one': '{{count}} full backup',
    'pitr.dayBarman_other': '{{count}} full backups',
    'pitr.dayDeploys_one': '{{count}} deployment',
    'pitr.dayDeploys_other': '{{count}} deployments',
    'pitr.dayConfigs_one': '{{count}} config change',
    'pitr.dayConfigs_other': '{{count}} config changes',
    'pitr.dayNothing': 'nothing recorded',
    'pitr.dayToday': 'ongoing',
    'pitr.eventsUnavailable':
      'GitLab history unavailable: deployments and configuration changes are not shown.',
    'pitr.forbidden':
      'Reserved to administrators (and to the project’s editors on dev).',
    'pitr.reasonNoTemplates':
      'Point-in-time restore is not set up for this environment yet.',
    'pitr.reasonNoCluster':
      'No active PostgreSQL database was found for this environment.',
    'pitr.reasonNoArchiving':
      'Continuous archiving of the database logs is not active: no point in time can be restored.',
    'pitr.reasonFilesOnVolume':
      'The attachments are on a volume: point-in-time restore will be available once they are on S3.',
    'pitr.reasonPending':
      'A restore is awaiting validation: validate or cancel it first.',
    'pitr.unavailableButton': 'Restore to a point in time…',
    'pitr.pendingChip': 'awaiting validation',
    'pitr.pendingInfo':
      'Another restore will be possible after validation or cancellation.',
    'pitr.historyToggle': 'Restore history',
    'pitr.historyEmpty': 'No restore among the {{limit}} most recent tasks.',
    'pitr.historyFailed': 'The history could not be read.',
    'pitr.historyNote':
      'Among the {{limit}} most recent tasks you are allowed to see.',
    'pitr.historyDate': 'Date',
    'pitr.historyOperation': 'Operation',
    'pitr.historyStatus': 'Result',
    'pitr.historyTask': 'Task',
    'pitr.historyTaskLink': 'task',
    'pitr.historyRestore': 'Back to {{date}}',
    'pitr.historyRestoreNoDate': 'Restore',
    'pitr.historyValidate': 'Validation',
    'pitr.historyCancel': 'Cancellation',
    'pitr.statusRunning': 'running',
    'pitr.statusCompleted': 'done',
    'pitr.statusFailed': 'failed',
    'pitr.statusCancelled': 'cancelled',
    'pitr.headerBadge': 'restore to validate',
    'pitr.bannerTitle': 'Restored to {{date}} (Paris time) — to be validated.',
    'pitr.bannerTitleNoDate': 'Restore to be validated.',
    'pitr.bannerText':
      'The site runs on the restored database. The old database is kept: “Cancel” goes back to it, with the media as they were before the restore. “Validate” deletes it, with its snapshots.',
    'pitr.bannerOverview':
      'Restored to {{date}} (Paris time) — to be validated in the Data tab.',
    'pitr.bannerOverviewNoDate':
      'A restore is to be validated in the Data tab.',
    'pitr.showData': 'View in Data',
    'pitr.cancelRestore': 'Cancel the restore',
    'pitr.validate': 'Validate',
    'pitr.validateTitle': 'Validate the restore of {{branch}}',
    'pitr.validateText':
      'The old database and its snapshots will be deleted: the restore can no longer be cancelled afterwards.',
    'pitr.validateSubmit': 'Validate the restore',
    'pitr.cancelTitle': 'Cancel the restore of {{branch}}',
    'pitr.cancelText':
      'The site will be stopped, the media brought back to how they were before the restore and the old database put back in service, then the site restarted. What was written since the restore will be lost.',
    'pitr.back': 'Back',
    'pitr.dayTitle': 'Restore {{branch}} to a point in time',
    'pitr.daySubtitle': '{{project}} · Paris time',
    'pitr.previousDay': 'Previous day, {{day}}',
    'pitr.nextDay': 'Next day, {{day}}',
    'pitr.hourLabel': 'Hour',
    'pitr.hourCell': '{{hour}}:00',
    'pitr.hourCellRepeat': '{{hour}}:00, second time (winter time)',
    'pitr.markerSnapshot': 'Snapshot',
    'pitr.markerBarman': 'Full backup',
    'pitr.legend':
      '◇ snapshot · ● full backup · ■ configuration · ▲ deployment · ⇢ database switch · lighter: partly restorable · dimmed: will be undone · hatched: not restorable',
    'pitr.exactTime': 'Exact time (HH:MM:SS, Paris)',
    'pitr.minuteSlider': 'Minute of {{hour}}:00',
    'pitr.timeInvalid': 'Type a time as HH:MM:SS.',
    'pitr.timeMissing':
      'This time does not exist on that day (change to summer time).',
    'pitr.timeOutside': 'Outside the restorable window.',
    'pitr.eventDeploy': '{{time}} deployment {{label}}',
    'pitr.eventConfig': '{{time}} configuration',
    'pitr.eventConfigBy': '{{time}} configuration ({{by}})',
    'pitr.planLoading': 'Checking this point in time…',
    'pitr.planFailed': 'The preview could not be read.',
    'pitr.planDatabase': 'Database',
    'pitr.planZone': 'zone {{zone}}',
    'pitr.planMedia': 'Media',
    'pitr.planMediaAt': 'brought back to {{date}}',
    'pitr.planNoBucket': 'No data bucket: database only.',
    'pitr.planSite': 'Site',
    'pitr.refusalInvalid': 'This point in time is not valid.',
    'pitr.refusalFuture': 'This point in time is in the future.',
    'pitr.refusalBeforeWindow': 'Before the start of the restorable window.',
    'pitr.refusalNoSource':
      'No database backup before this point in time: pick a later one.',
    'pitr.refusalNotFound': 'This environment cannot be found any more.',
    'pitr.refusalUnavailable':
      'Point-in-time restore is unavailable for this environment.',
    'pitr.utc': 'i.e. {{utc}}',
    'pitr.simulate': 'Simulate the bucket',
    'pitr.simulating': 'Simulating…',
    'pitr.simulationSummary':
      'Simulation: {{hide}} to hide, {{restore}} to restore, {{keep}} unchanged.',
    'pitr.simulationErrors': 'Errors: {{errors}}.',
    'pitr.simulationFailed': 'The simulation failed.',
    'pitr.simulationTooRecent':
      'Too recent to simulate: pick a time at least one minute in the past.',
    'pitr.simulationRunning':
      'A simulation is already running for this environment.',
    'pitr.simulationUnavailable':
      'Simulation unavailable for this environment.',
    'pitr.close': 'Close',
    'pitr.confirmTitle': 'Confirm the restore of {{branch}} to {{date}}',
    'pitr.confirmSubtitle': '{{utc}} · Paris time',
    'pitr.confirmStop':
      'The site of {{branch}} will be stopped during the operation, then restarted.',
    'pitr.confirmMedia':
      'The media will be brought back to {{date}}; files added since will be hidden (recoverable).',
    'pitr.confirmDatabaseOnly':
      'Only the database is brought back: the files are not.',
    'pitr.confirmDecide':
      'I will validate or cancel afterwards: the old database is kept until then.',
    'pitr.confirmSubmit': 'Restore for good',
    // Older generations (plan Plateforme-pitr-b1-a2, step 5): the window
    // covers the retention, across the restores validated in it.
    'pitr.dayNotRestorable': 'Nothing on this day can be restored.',
    // The day cards in 24 hours (mockup « v2-jours », 2026-11-03): the
    // accessible name of a card, the banner of the periods that cannot be
    // restored, its « Why? » panel, the legend.
    'pitr.daySwitchFirst': 'switch to g{{generation}} at {{time}}',
    'pitr.daySwitchNext': 'to g{{generation}} at {{time}}',
    'pitr.and': 'and',
    // The day dialog's band and preview (mockups « p1-ruban », « p2-apercu »).
    'pitr.bandCurrent': 'g{{generation}} · in service',
    'pitr.bandUnknown': '?',
    'pitr.lostCallout':
      'Everything done after {{day}} at {{time}} will be lost',
    'pitr.badgeSnapshot': 'snapshot',
    'pitr.badgeOlder': 'old database g{{generation}}',
    'pitr.badgeFull': 'full backup',
    'pitr.badgeBucket': 'S3 bucket',
    'pitr.badgeSite': 'stopped during the operation',
    'pitr.planBaseSnapshot': 'snapshot of {{date}} + logs up to {{time}}',
    'pitr.planBaseFull': 'full backup of {{date}} + logs up to {{time}}',
    'pitr.planBaseOlder':
      'full backup of {{date}} at the earliest + logs up to {{time}} · longer restore',
    'pitr.planBaseNext':
      'new database g{{next}}, g{{current}} kept until validation',
    'pitr.planSiteStop': 'stopped during the operation, then restarted',
    'pitr.planSiteKept': 'current image and configuration kept',
    'pitr.periodsHide': 'Hide the details',
    // The compact day dialog (mockup « p4-fenetre-compacte »).
    'pitr.dayNav': 'Day',
    'pitr.bandLost': 'g{{generation}} ✕',
    'pitr.bandLostLong': 'g{{generation}}, not restorable: {{reason}}',
    'pitr.bandUnknownLong': 'unknown database',
    'pitr.legendFull': 'Full legend',
    'pitr.chipSnapshot': 'snapshot',
    'pitr.chipBarman': 'full backup',
    'pitr.chipConfig': 'configuration',
    'pitr.chipDeploy': 'deployment',
    'pitr.stepMinusHour': '−1 h',
    'pitr.stepMinusMinute': '−1 min',
    'pitr.stepPlusMinute': '+1 min',
    'pitr.stepPlusHour': '+1 h',
    'pitr.stepMinusHourName': 'One hour earlier',
    'pitr.stepMinusMinuteName': 'One minute earlier',
    'pitr.stepPlusMinuteName': 'One minute later',
    'pitr.stepPlusHourName': 'One hour later',
    'pitr.justBefore': 'Just before:',
    'pitr.beforeSnapshot': 'snapshot of {{time}}',
    'pitr.beforeBarman': 'full backup of {{time}}',
    'pitr.beforeConfig': 'configuration of {{time}}',
    'pitr.beforeDeploy': 'deployment of {{time}}',
    'pitr.simulateInline': 'simulate',
    'pitr.simulateHint': 'to see what would be hidden',
    'pitr.restoreAt': 'Restore to {{time}}…',
    'pitr.tick0': '0h',
    'pitr.tick12': '12h',
    'pitr.tick24': '24h',
    'pitr.dayGaps': 'not restorable {{ranges}}',
    'pitr.dayGapRange': 'from {{from}} to {{to}}',
    'pitr.dayLabelGaps': '{{main}}; {{gaps}}',
    'pitr.periodsTitle_one':
      '{{count}} non-restorable period in the retention: {{periods}}.',
    'pitr.periodsTitle_other':
      '{{count}} non-restorable periods in the retention: {{periods}}.',
    'pitr.periodsWhy': 'Why?',
    'pitr.periodsPanelTitle_one': '{{count}} non-restorable period',
    'pitr.periodsPanelTitle_other': '{{count}} non-restorable periods',
    'pitr.periodEndUnsure':
      'end of g{{generation}} not guaranteed (logs just before its switch)',
    'pitr.periodNoBarmanYet': 'g{{generation}}: no full backup yet',
    'pitr.periodNoBarman': 'g{{generation}}: no full backup at all',
    'pitr.periodSwitch': 'g{{generation}}: switch, not backed up yet',
    'pitr.periodNoBackup': 'g{{generation}}: no backup yet',
    'pitr.periodUnknown': 'database in service unknown',
    'pitr.periodsFuture':
      'Every new database now gets a full backup as soon as it is created: these gaps will not happen again.',
    'pitr.legendGeneration': 'g{{generation}}',
    'pitr.legendCurrent': 'g{{generation}} (in service)',
    'pitr.legendGap': 'not restorable',
    'pitr.legendIcons':
      '◇ snapshot · ● full backup · ■ configuration · ▲ deployment',
    'pitr.markerSwitch': 'Switch to generation {{generation}} · {{time}}',
    'pitr.hourCellGap': '{{hour}}:00, not restorable: {{reason}}',
    'pitr.refusalNotRestorable': 'Not restorable: {{reason}}',
    'pitr.gapNoBarman':
      'no full backup (Barman) of generation {{generation}} covers this time; its snapshots went with it.',
    'pitr.gapSwitch':
      'the database had just been replaced by a restore and had no backup yet.',
    'pitr.gapNoBackup': 'no database backup exists before this time.',
    'pitr.gapEndUnsure':
      'end of the period not guaranteed: the logs of that database may not reach this time (just before its switch).',
    'pitr.eventsTruncated':
      'Too many deployments and configuration changes: the oldest are not shown.',
    'pitr.gapUnknown':
      'which database was in service at this time is not known.',
  },
});

/**
 * The French messages. `full: true` makes exhaustiveness a compile error, so a
 * key added to the ref above without a translation here fails the build rather
 * than falling back to English at runtime.
 *
 * Typography: the typographic apostrophe (’, U+2019) everywhere, never the
 * straight one. Uniform inside this file on purpose — the card and the page
 * show these strings side by side, and two shapes of apostrophe on one screen
 * read as a typo. Note that @waadoo/backstage-i18n still uses the straight one
 * for the upstream plugins; aligning that package is a separate pass.
 */
const resource = createTranslationMessages({
  full: true,
  ref: projectEnvironmentsTranslationRef,
  messages: {
    'card.title': 'Environnements',
    'card.subtitle': 'Un environnement {{app}} par branche de développement',
    'card.refresh': 'Rafraîchir',
    'card.empty':
      'Aucun environnement pour ce projet. Poussez une branche `dev-…` pour en créer un.',
    'card.emptyDelay':
      'Les environnements d’un projet tout juste créé peuvent mettre quelques minutes à apparaître.',
    'card.permanentCount_one': '{{count}} branche permanente',
    'card.permanentCount_other': '{{count}} branches permanentes',
    'card.deployedCount_one': '{{count}} déployée',
    'card.deployedCount_other': '{{count}} déployées',
    'sleeping.badge': 'En sommeil',
    'sleeping.activatableByEditors': 'réveil par les éditeurs',
    'sleeping.activatableByAdmins': 'réveil par les administrateurs',
    'phone.cpu': 'CPU',
    'phone.memory': 'Mém.',
    'phone.sleeping': 'en sommeil',
    'phone.wakeAdmins': 'réveil admin',
    'phone.wakeEditors': 'réveil éditeurs',
    'phone.newBranch': 'Nouvelle branche éphémère, depuis {{source}}',
    'phone.quota': '{{used}}/{{max}}',
    'phone.totalLine': 'CPU {{cpu}} · Mém. {{memory}} · Vol. {{volumes}}',
    'phone.partialLabel': 'Environnements lisibles seuls :',
    'phone.moreActions': 'Autres actions',
    'phone.sheetTitle': 'Actions de la branche',
    'phone.close': 'Fermer',
    'sleeping.adminTag': 'admin',
    'tier.productionDescription': 'Production',
    'tier.stagingDescription': 'Préproduction',
    'tier.developmentDescription': 'Branche d’intégration',
    'tier.ephemeralDescription': 'Branche éphémère',
    'sleeping.title': 'Cet environnement est en sommeil',
    'sleeping.description':
      'La branche {{branch}} est une branche permanente du projet, mais aucun environnement {{app}} n’est déployé pour elle.',
    'sleeping.pipeline': 'Pipeline #{{number}} · {{status}}',
    'sleeping.pipelineNoNumber': 'Dernier pipeline · {{status}}',
    'sleeping.plannedAddress': 'Adresse prévue',
    'sleeping.plannedAddressLabel': 'Adresse prévue : {{address}}',
    'envPower.wake': 'Réveiller',
    'envPower.wakeBranch': 'Réveiller {{branch}}',
    'envPower.sleep': 'Mettre en sommeil',
    'envPower.adminsOnly': 'Réservé aux administrateurs',
    'envPower.devLocked':
      'dev ne peut pas être mis en sommeil : ses branches éphémères en dépendent',
    'envPower.rightsLine':
      'Éditeurs du projet et administrateurs. La production : administrateurs seulement.',
    'envPower.cancel': 'Annuler',
    'envPower.launchFailed': 'La tâche n’a pas pu être lancée : {{message}}',
    'envPower.confirmLabel': 'Tapez {{confirmation}} pour confirmer',
    'wake.title': 'Réveiller {{branch}}',
    'wake.subtitleEditors': '{{project}} · réservé aux éditeurs du projet',
    'wake.subtitleAdmins': '{{project}} · réservé aux administrateurs',
    'wake.createdTitle': 'Ce qui va être créé',
    'wake.itemEnvironment': 'Environnement {{namespace}}',
    'wake.itemEnvironmentDetail': 'namespace, Application ArgoCD, réseau',
    'wake.itemDatabase': 'Base PostgreSQL et fichiers {{app}}',
    'wake.itemDatabaseDetail':
      'créés vides, puis remplis selon le choix ci-dessous',
    'wake.itemDatabaseOnly': 'Base PostgreSQL de {{app}}',
    'wake.itemDatabaseOnlyDetail':
      'créée vide, puis remplie selon le choix ci-dessous',
    'wake.itemMedia': 'Médias',
    'wake.itemMediaKept': 'bucket S3 conservé, repris tel quel',
    'wake.itemMediaEmptied': 'bucket S3 conservé, vidé avant l’activation',
    'wake.itemBackups': 'Sauvegardes',
    'wake.itemBackupsDetail':
      'bucket S3 existant repris, planification quotidienne',
    'wake.itemAddress': 'Adresse',
    'wake.dataTitle': 'Données de départ',
    'wake.dataLatest': 'Restaurer la dernière sauvegarde de {{branch}}',
    'wake.dataLatestDetail':
      'la plus récente si elle existe — celle prise à la mise en sommeil ; sinon, la base neuve est conservée',
    'wake.dataFresh': 'Base neuve',
    'wake.dataFreshDetail':
      'installation vide (données de démonstration selon le projet)',
    'wake.duration':
      'Durée : 5 à 10 min, plus le premier pipeline si la branche n’a pas encore d’image. Suivi dans « Tâches ».',
    'wake.durationStandard': 'Durée : quelques minutes. Suivi dans « Tâches ».',
    'wake.submit': 'Réveiller {{branch}}',
    'sleep.title': 'Mettre {{branch}} en sommeil',
    'sleep.subtitle': '{{project}} · éditeurs (prod : administrateurs)',
    'sleep.stepBackupTitle': 'Sauvegarde finale',
    'sleep.stepBackupText':
      'de la base et des fichiers vers le bucket S3, vérifiée avant de continuer',
    'sleep.stepStopTitle': 'Arrêt :',
    'sleep.stepStopText':
      '{{app}}, PostgreSQL et Valkey supprimés, plus rien n’est réservé sur le cluster',
    'sleep.stepKeptTitle': 'Conservé :',
    'sleep.stepKeptText':
      'la branche {{branch}}, sa CI, sa configuration (capacité, HA, volumes) et toutes ses sauvegardes',
    'sleep.bucketNote':
      'Le bucket de sauvegarde de {{branch}} n’est jamais supprimé par la mise en sommeil : ses sauvegardes restent, la plus récente étant celle de l’étape 1.',
    'sleep.lostWrites':
      'Ce qui est modifié dans {{app}} après la sauvegarde finale est perdu : prévenir d’abord les utilisateurs de {{branch}}.',
    'sleep.submit': 'Sauvegarder puis mettre en sommeil',
    'sleep.stepNoBackupTitle': 'Sans sauvegarde :',
    'sleep.stepNoBackupText':
      'ce type d’application n’a pas de sauvegardes : la mise en sommeil n’est permise que pour un environnement sans données (aucun volume), la tâche la refuse sinon',
    'sleep.stepStopTextNoBackups':
      '{{app}} supprimé, plus rien n’est réservé sur le cluster',
    'sleep.stepKeptTextNoBackups':
      'la branche {{branch}} et sa configuration (capacité, HA)',
    'sleep.submitNoBackups': 'Mettre en sommeil',
    'reauth.title': 'Reconnexion nécessaire',
    'reauth.description':
      'Vos droits sur ce projet ont changé depuis votre connexion (par exemple parce que vous venez de le créer). Reconnectez-vous pour voir ses environnements.',
    'reauth.button': 'Se reconnecter',
    'column.branch': 'Branche',
    'column.ci': 'CI',
    'column.health': 'État',
    'column.url': 'Adresse',
    'column.links': 'Liens',
    // Adjectival forms, agreeing with the implied masculine « pipeline », so the
    // six cell labels share one register instead of mixing nouns (Succès,
    // Échec) with participles (Annulé, Ignoré). It is also the wording GitLab's
    // own French UI uses for these statuses. The cell may link to GitHub
    // instead, whose French UI says « exécutions » rather than « pipelines » —
    // deliberately not followed here: the column is read as one list, and
    // labelling two rows differently because their branches live on different
    // forges would suggest a difference in status, not in hosting.
    'ci.success': 'Réussi',
    'ci.failed': 'Échoué',
    'ci.running': 'En cours',
    'ci.pending': 'En attente',
    'ci.canceled': 'Annulé',
    'ci.skipped': 'Ignoré',
    'ci.unavailable': 'Indisponible',
    // Same adjectival register as the CI labels above: the three status columns
    // are read side by side on one row, so « Réussi | Sain | Synchronisé » has
    // to hold together as a sentence fragment.
    //
    // « Absent » rather than a literal « Manquant » for Missing: ArgoCD means
    // the resource is not in the cluster, which is what absent says in French.
    'health.healthy': 'Sain',
    'health.progressing': 'En cours',
    'health.degraded': 'Dégradé',
    'health.suspended': 'Suspendu',
    'health.missing': 'Absent',
    'health.unknown': 'Inconnu',
    'sync.synced': 'Synchronisé',
    'sync.outOfSync': 'Désynchronisé',
    'sync.unknown': 'Inconnu',
    'row.openInArgocd': 'Ouvrir dans ArgoCD',
    'row.openTerminals': 'Ouvrir un terminal',
    'row.openInGrafana': 'Monitoring (Grafana)',
    'row.openPipelines': 'Pipelines CI/CD de cette branche',
    'row.openPipelineRun': 'Ouvrir ce pipeline',
    'row.openApp': 'Ouvrir {{app}} ({{branch}})',
    'pipeline.succeeded': 'Pipeline réussi sur {{branch}}',
    'pipeline.failed': 'Pipeline en échec sur {{branch}}',
    'row.syncStatus': 'Synchronisation : {{status}}',
    // Le texte visible des liens vers les outils (ExternalToolLink.tsx) : le
    // nom de l’outil, rien de plus. La phrase complète (`backups.openInArgo`,
    // `logs.openInGrafana`…) en est l’infobulle et le nom accessible, et
    // contient ce mot.
    'link.workflows': 'Workflows',
    'link.argocd': 'ArgoCD',
    'link.grafana': 'Grafana',
    'link.pipelines': 'Pipelines',
    'tile.listLabel': 'Liens de cette branche',
    'tile.repository': 'Code source',
    'tile.repositorySubtitle': '{{forge}} · {{branch}}',
    'tile.openRepository': 'ouvrir le code source de cette branche',
    'tile.openPipelines': 'ouvrir les pipelines CI/CD de cette branche',
    'tile.pipelineRunning': 'En cours',
    'tile.pipelineRunningSince': 'En cours · depuis {{elapsed}}',
    'tile.pipelineFailed': 'Dernier en échec',
    'tile.pipelineRunningName': 'pipeline en cours',
    'tile.pipelineRunningSinceName': 'pipeline en cours depuis {{elapsed}}',
    'tile.pipelineFailedName': 'dernier pipeline en échec',
    'tile.elapsedUnderMinute': 'moins d’une minute',
    'tile.elapsedMinutes': '{{minutes}} min',
    'tile.elapsedHours': '{{hours}} h {{minutes}}',
    'tile.argocdSubtitle': 'Application de la branche',
    'tile.openArgocd': 'ouvrir l’application de cette branche dans ArgoCD',
    'tile.monitoring': 'Monitoring',
    'tile.monitoringSubtitle': 'Grafana · {{namespace}}',
    'tile.openMonitoring': 'ouvrir le monitoring de cette branche dans Grafana',
    'terminal.sectionApp': 'Application',
    'terminal.sectionDb': 'Base de données',
    'terminal.loading': 'Chargement des pods…',
    'terminal.empty': 'Aucun pod en cours d’exécution',
    'terminal.error': 'Pods indisponibles',
    // Le terminal intégré sous la liste des pods de l’aperçu.
    'terminal.open': 'Terminal',
    'terminal.openFor': 'Ouvrir un terminal dans {{pod}}',
    'terminal.regionLabel': 'Terminal de {{pod}}',
    'terminal.container': 'Conteneur',
    'terminal.containerLabel': 'Conteneur :',
    'terminal.openMarkerOne': '(terminal ouvert)',
    'terminal.openMarkerTwo': '(deux terminaux ouverts)',
    'terminal.openTooltip': 'Ouvrir un terminal',
    'terminal.close': 'Fermer',
    'terminal.duplicate': 'Dupliquer',
    'terminal.duplicateFor': 'Ouvrir un second terminal dans {{pod}}',
    'terminal.replaced':
      'Deux terminaux au plus : celui de {{pod}} a été fermé.',
    'terminal.containersLoading': 'Chargement des conteneurs…',
    'terminal.containersError':
      'Les conteneurs de ce pod ne peuvent pas être lus.',
    'terminal.loadingView': 'Chargement du terminal…',
    'terminal.connecting': 'Connexion…',
    'terminal.renewed': 'Session renouvelée — nouveau shell',
    'terminal.idleWarning': 'Session inactive — fermeture dans {{seconds}} s',
    'terminal.keepAlive': 'Garder la session',
    'terminal.restart': 'Relancer',
    'terminal.end.ended': 'Session terminée',
    'terminal.end.idle': 'Session fermée après 20 minutes d’inactivité',
    'terminal.end.limitUser':
      'Vous avez déjà le nombre maximal de terminaux ouverts : fermez-en un d’abord.',
    'terminal.end.limitTotal':
      'Trop de terminaux sont ouverts sur la plateforme : réessayez dans un moment.',
    'terminal.end.refused': 'L’accès à ce terminal est refusé.',
    'terminal.end.argocdDenied':
      'ArgoCD refuse d’ouvrir ce terminal (droits insuffisants).',
    'terminal.end.argocdRejected':
      'ArgoCD n’a pas pu ouvrir le terminal (pod arrêté, conteneur absent ou terminal désactivé).',
    'terminal.end.upstreamError': 'ArgoCD est injoignable.',
    'terminal.end.shutdown':
      'Session interrompue par un redémarrage de Backstage.',
    'terminal.end.tokenExpired':
      'Votre session Authelia a expiré : rechargez la page pour vous reconnecter.',
    'terminal.end.unavailable': 'Le terminal est injoignable.',
    'terminal.unresponsive': 'Le terminal ne répond pas.',
    'terminal.lost': 'Connexion au terminal perdue.',
    'terminal.retry': 'Réessayer',
    // La page par branche.
    'page.revision': 'Révision',
    // « Elle […] supprimée » : le pronom et l’accord reprennent la branche, pas
    // l’environnement. C’est la branche qui disparaît — l’environnement n’est
    // que ce qu’ArgoCD en déployait.
    //
    // La même phrase sert aux deux moments où l’on peut la constater : aucune
    // Application ne porte ce nom de branche, et l’Application a disparu entre
    // la requête liste et la requête détail. Les distinguer n’apprendrait rien
    // à qui lit l’écran.
    'page.notFound':
      'Aucun environnement pour la branche {{branch}} du projet {{project}}. Elle vient peut-être d’être supprimée.',
    // Le premier élément du fil d’Ariane de la page projet, et le nom et
    // l’infobulle du bouton maison de la page branche : le même mot que
    // l’entrée « Accueil » du menu.
    'page.homeCrumb': 'Accueil',
    'page.backToProject': 'Retour au projet {{project}}',
    // Voir la clé anglaise : la locale des dates voyage avec ces messages,
    // puisque la langue de l’application reste `en`. « fr-FR » donne
    // `01/10/2026 00:00`.
    'format.locale': 'fr-FR',
    // Le bandeau de synthèse sous l’en-tête, visible quel que soit l’onglet.
    'band.openApp': 'Ouvrir {{app}}',
    'band.images': 'Images',
    'band.lastDeploy': 'Dernier déploiement',
    // « +N » en tête du nom accessible, comme à l’écran : voir la clé anglaise.
    'band.otherImages_one': '+{{count}} autre image',
    'band.otherImages_other': '+{{count}} autres images',
    'band.fullReferences': 'Références complètes des images',
    'band.hideImages': 'Masquer les références des images',
    'band.ciLoading': 'Chargement…',
    // Les onglets de la page par branche. Les mots des colonnes et des cartes
    // sont repris tels quels (« Logs », « Emails ») : l’onglet et la carte
    // qu’il ouvre doivent porter le même nom. « Données » regroupe les
    // sauvegardes et l’envoi de fichiers, c’est-à-dire ce qui entre dans
    // l’environnement ou en sort.
    'tab.listLabel': 'Sections de cet environnement',
    'tab.overview': 'Aperçu',
    'tab.logs': 'Logs',
    'tab.emails': 'Emails',
    'tab.data': 'Données',
    'tab.history': 'Historique',
    'tab.access': 'Accès',
    'tab.configuration': 'Configuration',
    // L’onglet Aperçu.
    'overview.pods': 'Pods',
    'overview.columnPod': 'Pod',
    'podName.postgres': 'PostgreSQL {{instance}}',
    'podName.mariadbInit': 'Job d’init {{label}}',
    'podName.withId': '{{label}} · {{id}}',
    'overview.columnActions': 'Actions',
    'overview.columnStarted': 'Démarré',
    'overview.restarts_one': '{{count}} redémarrage',
    'overview.restarts_other': '{{count}} redémarrages',
    'overview.zoneBadge': 'Zone {{letter}}',
    'overview.zoneBadgeUnknown': 'Zone ?',
    'overview.zoneTooltip': '{{zone}} · nœud {{node}}',
    'overview.zoneUnknown': 'Nœud {{node}}, zone non renseignée',
    'overview.zonePending': 'En attente de placement',
    'overview.zoneName': '{{label}} — {{text}}',
    'overview.haBadge': 'HA',
    'overview.haAutoscaling':
      'Haute disponibilité : {{min}} à {{max}} pods (autoscaling)',
    'overview.haAutoscalingRequired':
      'Haute disponibilité : {{min}} à {{max}} pods (autoscaling), répartition stricte sur les zones',
    'overview.haAutoscalingPreferred':
      'Haute disponibilité : {{min}} à {{max}} pods (autoscaling), répartis sur les zones si possible',
    'overview.haReplicas': 'Haute disponibilité : {{pods}} pods',
    'overview.haReplicasRequired':
      'Haute disponibilité : {{pods}} pods, répartition stricte sur les zones',
    'overview.haReplicasPreferred':
      'Haute disponibilité : {{pods}} pods, répartis sur les zones si possible',
    'overview.haInstancesSync':
      'Haute disponibilité : {{instances}} instances PostgreSQL, réplication synchrone',
    'overview.haInstancesAsync':
      'Haute disponibilité : {{instances}} instances PostgreSQL, réplication asynchrone',
    'overview.dbPrimary': 'Primaire',
    'overview.dbPrimaryTooltip': 'Instance principale : lectures et écritures',
    'overview.dbReplica': 'Réplica · lecture',
    'overview.dbReplicaTooltip':
      'Copie en lecture seule, prête à prendre le relais',
    'overview.dbRoleName': '{{label}} — {{text}}',
    'overview.postgresVersion': 'PostgreSQL {{version}} · image {{image}}',
    'overview.hpaBadge': 'HPA {{min}}–{{max}}',
    'overview.hpaRange': 'Autoscaling : {{min}} à {{max}} pods',
    'overview.hpaCurrent': 'actuellement {{current}}',
    'overview.hpaCpu': 'CPU {{current}} % pour une cible de {{target}} %',
    'overview.hpaCpuTarget': 'cible CPU {{target}} %',
    'overview.hpaCpuCurrent': 'CPU {{current}} %',
    'overview.hpaScaleDown': 'réduction après {{window}}',
    'overview.durationMinutes': '{{minutes}} min',
    'overview.durationSeconds': '{{seconds}} s',
    'overview.recentDeployments': 'Derniers déploiements',
    // La carte Emails, seule dans l’onglet Emails : ce que le Mailpit de
    // l’environnement a capturé. « Capturés » et jamais « envoyés » ni
    // « reçus » : le propos de la carte est qu’aucun n’est parti.
    'mails.title': 'Emails',
    'mails.subtitle':
      'Les emails envoyés par cet environnement sont capturés ici, pas distribués. Ils disparaissent au redémarrage de l’environnement (500 au plus).',
    'mails.notConfigured': 'Aucune capture d’emails pour cet environnement.',
    'mails.empty': 'Aucun email capturé pour l’instant.',
    'mails.unavailable': 'Les emails ne peuvent pas être lus pour le moment.',
    'mails.retry': 'Réessayer',
    // La recherche au-dessus de la liste, lancée par Entrée et non à la
    // frappe : voir MailsCard.tsx. Les opérateurs sont ceux de Mailpit et
    // restent en anglais.
    'mails.searchLabel': 'Rechercher',
    'mails.searchPlaceholder': 'ex. subject:devis',
    'mails.searchHint':
      'Texte libre ou opérateurs (from:, to:, subject:), puis Entrée.',
    'mails.noMatch': 'Aucun email ne correspond.',
    // Le tableau des emails, dont les en-têtes sont `mails.date`,
    // `columnFrom`, `columnTo` et `columnSubject`. Le bouton de chaque ligne
    // porte la phrase ci-dessous, destinataires compris : la colonne À est
    // masquée quand la carte est étroite. « capturé » et non « reçu », pour
    // la raison de `mails.title`.
    'mails.listLabel': 'Emails capturés',
    'mails.rowLabel': '{{subject}}, de {{from}} à {{to}}, capturé le {{date}}',
    // La pagination sous la liste : « 1–10 sur 57 », avec un tiret demi-cadratin.
    'mails.pagination': 'Pages de la liste des emails',
    'mails.range': '{{from}}–{{to}} sur {{total}}',
    'mails.previous': 'Précédent',
    'mails.next': 'Suivant',
    'mails.columnFrom': 'De',
    'mails.columnTo': 'À',
    'mails.columnSubject': 'Objet',
    'mails.noSubject': '(sans objet)',
    'mails.back': 'Retour à la liste',
    'mails.cc': 'Cc',
    'mails.date': 'Date',
    'mails.attachments': 'Pièces jointes (non téléchargeables ici) : {{files}}',
    'mails.viewHtml': 'HTML',
    'mails.viewText': 'Texte',
    'mails.frameTitle': 'Contenu de l’email',
    'mails.emptyBody': '(corps vide)',
    'mails.messageGone':
      'Cet email n’existe plus : Mailpit ne conserve que les 500 plus récents.',
    'mails.messageUnavailable': 'Cet email ne peut pas être lu pour le moment.',
    // Le sélecteur de branche de l’en-tête. « Changer de branche » sur le
    // bouton — l’infinitif de l’action, comme « Ouvrir un terminal » ; jamais
    // « Basculer », qui en français suggère un état permuté et non un
    // déplacement d’un écran à l’autre.
    'page.switchBranch': 'Changer de branche',
    // Le texte visible du bouton, entre l’icône de branche et le chevron ;
    // « Changer de branche » en reste le nom accessible, qui le contient.
    'page.switchBranchShort': 'Branche',
    'page.branches': 'Branches',
    // Le marqueur des branches éphémères (BranchLabel.tsx) : le nom accessible
    // et l’infobulle de la pastille au fantôme, et les mots qu’elle montre
    // après le fantôme à partir de 14 jours d’inactivité. « inactive »
    // s’accorde avec la branche, « inactif » avec l’environnement. Durées en
    // jours entiers, « N j », sans forme plurielle (voir la clé anglaise).
    'branch.ephemeral': 'Branche éphémère',
    'branch.ephemeralInactive': 'Branche éphémère, inactive ?',
    'branch.inactive': 'inactive ?',
    'branch.description': 'Environnement éphémère, créé {{age}}',
    'branch.descriptionInactive':
      'Environnement éphémère, créé {{age}}, inactif depuis {{idle}} j ?',
    'branch.descriptionNoAge': 'Environnement éphémère',
    'branch.descriptionInactiveNoAge':
      'Environnement éphémère, inactif depuis {{idle}} j ?',
    'branch.banner':
      'Environnement de test : il est supprimé avec la branche, ses données ne sont pas sauvegardées.',
    'page.age': 'Créé',
    'age.today': 'aujourd’hui',
    'age.yesterday': 'hier',
    'age.daysAgo': 'il y a {{days}} j',
    'history.title': 'Historique des déploiements',
    'history.subtitle':
      'Chaque révision déployée par ArgoCD sur cette branche, de la plus récente à la plus ancienne',
    'history.columnRevision': 'Révision',
    // « Déployée » s'accorde avec « révision », la colonne voisine, et non avec
    // un « déploiement » masculin qui n'apparaît nulle part dans le tableau.
    'history.columnDeployedAt': 'Déployée',
    'history.empty': 'Aucun déploiement enregistré pour cette branche.',
    'history.openRevision': 'Ouvrir cette révision sur la forge',
    // « Logs » n'est pas traduit, pour la raison qui laisse « Healthy » ou
    // « Synced » en anglais plus haut : l'onglet ArgoCD vers lequel ce panneau
    // renvoie s'appelle LOGS, et « Journaux » ferait diverger les deux écrans.
    'logs.title': 'Logs',
    'logs.subtitle':
      '{{lines}} dernières lignes de {{pod}}, lues une seule fois',
    'logs.noPods':
      'Aucun pod en cours d’exécution dont on puisse lire les logs.',
    'logs.unavailable':
      'Les logs ne peuvent pas être affichés ici. Explorez-les dans Grafana.',
    'logs.openInGrafana': 'Explorer ces logs dans Grafana',
    'logs.jumpToLatest': 'Derniers logs',
    'logs.container': 'Conteneur',
    'logs.containerOption': '{{pod}} › {{container}}',
    'logs.containerOptionInit': '{{pod}} › {{container}} (init)',
    'logs.containerDefault': '{{pod}} › conteneur par défaut',
    'logs.previous': 'Avant le redémarrage',
    'logs.previousHelp':
      'Logs du conteneur avant son dernier redémarrage — là où se trouve l’erreur qui l’a arrêté.',
    'logs.previousUnavailable':
      'Aucun log d’une exécution précédente pour ce conteneur.',
    'logs.subtitleLive':
      '{{lines}} dernières lignes de {{pod}}, puis chaque nouvelle ligne dès son écriture',
    'logs.pause': 'Pause',
    'logs.resume': 'Reprendre',
    'logs.statusLive': 'En direct',
    'logs.statusPaused': 'En pause',
    'logs.statusReconnecting': 'Reconnexion…',
    'logs.waiting':
      'Aucune ligne pour l’instant : les nouvelles lignes s’afficheront ici dès leur écriture.',
    // « Rafraîchissez la page » : le verbe du bouton de l’en-tête
    // (`card.refresh`), qui relit l’arbre des ressources et rouvre le flux —
    // même règle que `upload.rejectedNoSuchPod`.
    'logs.podGone':
      'Ce pod n’existe plus : ses logs ne sont plus suivis. Rafraîchissez la page pour lire les pods actuels.',
    'logs.streamStopped':
      'Les logs ne sont plus suivis : ArgoCD a refusé le flux. Rafraîchissez la page pour réessayer.',
    // Le panneau des sauvegardes. « Inventaire » désigne partout l'exécution du
    // workflow, jamais « sauvegarde » : ce que le bouton lance est une lecture
    // du bucket, et l'appeler une sauvegarde laisserait croire que le clic en
    // crée une. « Bucket » n'est pas traduit — c'est le mot que le stockage
    // objet d'OVH emploie, y compris dans sa console française.
    'backups.title': 'Sauvegardes complètes',
    'backups.subtitle': 'Jeux de sauvegarde disponibles pour cet environnement',
    'backups.notConfigured':
      'Aucune sauvegarde configurée pour cet environnement.',
    'backups.noRun':
      'Aucun inventaire n’a encore été fait. Lancez-en un pour voir les sauvegardes disponibles.',
    'backups.running': 'Inventaire en cours…',
    'backups.lastRun': 'Dernier inventaire : {{date}}',
    'backups.runFailed': 'Le dernier inventaire a échoué.',
    // Affichée sous l’échec ci-dessus, au-dessus du tableau produit par une
    // exécution PRÉCÉDENTE. Elle doit dater : sans elle, le tableau se lit
    // comme le résultat de l’inventaire qui vient d’échouer.
    'backups.setsFromEarlierRun':
      'Inventaire du {{date}}, le dernier qui a abouti.',
    'backups.openInArgo': 'Ouvrir cette exécution dans Argo Workflows',
    'backups.openNamespaceInArgo':
      'Ouvrir les exécutions de cet environnement dans Argo Workflows',
    // Relance la lecture de la carte, pas l’inventaire — d’où « Réessayer »
    // plutôt qu’un mot du vocabulaire des inventaires, qui laisserait croire
    // que le clic déclenche un traitement.
    'backups.retry': 'Réessayer',
    'backups.noBackups':
      'Aucune sauvegarde dans le bucket pour cet environnement.',
    'backups.rawFallback':
      'Cet inventaire n’est pas revenu dans le format attendu. Le voici tel qu’il a été reçu.',
    'backups.logPruned':
      'Le journal de cet inventaire n’existe plus — Argo Workflows ne conserve que ses vingt dernières exécutions. Relancez-en un.',
    'backups.logUnreadable': 'Le journal de cet inventaire n’a pas pu être lu.',
    // Le tableau : une ligne par sauvegarde, nommée par un identifiant court
    // tiré de son horodatage UTC (`#AAMMJJ-HHMM`, voir backupSets.ts).
    'backups.columnId': 'ID',
    'backups.columnDate': 'Date',
    'backups.columnContent': 'Contenu',
    'backups.columnSize': 'Taille',
    'backups.columnActions': 'Actions',
    'backups.idUtcTitle': 'Heure UTC : {{time}}',
    'backups.age': '({{age}})',
    // Voir la clé anglaise : le nombre n’est jamais 0, et l’anglais et le
    // français s’accordent sur 1 comme sur 2 et plus.
    'backups.contentDatabases_one': '{{count}} base',
    'backups.contentDatabases_other': '{{count}} bases',
    'backups.contentVolumes_one': '{{count}} volume',
    'backups.contentVolumes_other': '{{count}} volumes',
    'backups.contentNoDatabase': 'aucune base',
    'backups.contentNoFile': 'aucun fichier',
    // Les unités sous le Mio, comme `resources.unitMiB` et `resources.unitGiB`.
    'backups.unitB': 'o',
    'backups.unitKiB': 'Kio',
    // Le nom accessible et l’infobulle du bouton, qui affiche
    // `backups.listingShort`.
    'backups.start': 'Lancer un inventaire des sauvegardes',
    // « Relancer », le verbe même de « Relancez-en un » dans
    // `backups.logPruned` : l’instruction et le bouton qui l’exécute doivent se
    // lire avec le même mot — ici dans l’infobulle et le nom accessible, le
    // bouton n’affichant que `backups.listingShort`. Surtout pas « Rafraîchir »,
    // qui en français désigne la relecture de l’affichage, alors que ce clic
    // crée un pod.
    'backups.rerun': 'Relancer un inventaire des sauvegardes',
    // « Inventaire » seul, sans verbe : le mot que reprennent l’infobulle et
    // le nom accessible ci-dessus, et que la ligne « Dernier inventaire : … »
    // affiche juste à côté.
    'backups.listingShort': 'Inventaire',
    'backups.alreadyRunning':
      'Un inventaire est déjà en cours pour cet environnement.',
    // L’infobulle du bouton désactivé : elle dit la règle, au présent, sans
    // reprocher quoi que ce soit à qui lit — c’est une question de droits, pas
    // une erreur de sa part.
    'backups.submitNotAllowed':
      'Le lancement d’un inventaire est réservé aux administrateurs, et aux éditeurs du projet sur dev et ses branches.',
    // Le 403, qui n’arrive que si les droits ont changé pendant que la carte
    // était ouverte. « Vous n’avez pas le droit » et non « Accès refusé » : la
    // phrase doit se lire comme une explication, pas comme un rejet technique.
    'backups.submitForbidden':
      'Vous n’avez pas le droit de lancer un inventaire pour cet environnement.',
    // L’infobulle du bouton d’inventaire pendant qu’une restauration tourne :
    // le miroir de `backups.restoreBlockedByListing` plus bas, et calquée sur
    // elle — une attente, pas un reproche.
    'backups.listingBlockedByRestore':
      'Attendez la fin de la restauration en cours avant de lancer un inventaire.',
    'backups.refreshFailed': 'L’inventaire n’a pas pu être lancé.',
    'backups.unavailable':
      'Les sauvegardes ne peuvent pas être lues pour le moment.',
    // La restauration, seul acte destructeur de cette carte. « Restaurer » pour
    // l’acte ; la confirmation dit ce qui est écrasé plutôt que de demander
    // « êtes-vous sûr ? », que personne ne lit.
    'backups.restore': 'Restaurer',
    'backups.restoreSet': 'Restaurer la sauvegarde {{id}}',
    'backups.restoreConfirmTitle':
      'Restaurer la sauvegarde {{id}} du {{date}} ?',
    'backups.restoreConfirmBody':
      'Les bases de données de la branche {{namespace}} seront écrasées par cette sauvegarde. Une sauvegarde de sécurité est prise juste avant.',
    // Le seul point qui ne se devine pas. « Pas forcément », parce que ça
    // dépend de l’environnement : le workflow n’arrête les charges que si la
    // restauration touche un PVC, et une branche Odoo déclare `pvcs: []` — donc
    // sur Odoo il n’est jamais arrêté, et la phrase doit valoir pour les deux.
    'backups.restoreNotStopped':
      '{{app}} n’est pas forcément arrêté pendant la restauration : redémarrez le pod une fois qu’elle est terminée.',
    // « Pour de bon » et non « Confirmer » : c’est le second clic, celui qui
    // écrase réellement, et il doit se lire comme tel.
    'backups.restoreConfirmAction': 'Restaurer pour de bon',
    'backups.restoreCancel': 'Annuler',
    'backups.restoreRunning': 'Restauration en cours…',
    // Le refus, et non l’état : l’infobulle du bouton désactivé d’une ligne
    // pendant qu’une restauration tourne. Calquée sur `backups.alreadyRunning`,
    // qui joue le même rôle pour l’inventaire — voir la règle énoncée au-dessus
    // de `backups.submitNotAllowed`.
    'backups.restoreAlreadyRunning':
      'Une restauration est déjà en cours pour cet environnement.',
    'backups.restoreSucceeded': 'La restauration est terminée.',
    'backups.restoreFailed': 'La dernière restauration a échoué.',
    'backups.restoreNotAllowed':
      'La restauration est réservée aux administrateurs, et aux éditeurs du projet sur dev et ses branches.',
    'backups.restoreNotConfigured':
      'Aucune restauration n’est configurée pour cet environnement.',
    'backups.restoreBlockedByListing':
      'Attendez la fin de l’inventaire en cours avant de restaurer.',
    'backups.restoreForbidden':
      'Vous n’avez pas le droit de restaurer une sauvegarde pour cet environnement.',
    'backups.restoreFailedToStart': 'La restauration n’a pas pu être lancée.',
    'backups.restoreLastRun': 'Dernière restauration : {{date}}',
    // La sauvegarde à la demande : la CronWorkflow nocturne, lancée tout de
    // suite. « Sauvegarde » pour cet acte-là et jamais « inventaire » — ce clic
    // crée bel et bien une sauvegarde, c’est précisément la confusion que le
    // vocabulaire de l’inventaire évite plus haut.
    // Le nom accessible et l’infobulle du bouton, qui affiche
    // `backups.backupShort`.
    'backups.backupNow': 'Sauvegarder maintenant',
    'backups.backupShort': 'Sauvegarder',
    'backups.backupRunningLabel': 'Sauvegarde en cours…',
    'backups.backupConfirmTitle':
      'Lancer une sauvegarde de {{namespace}} maintenant ?',
    'backups.backupConfirmBody':
      'Elle s’ajoute aux sauvegardes existantes, rien n’est écrasé.',
    'backups.backupConfirmRetention':
      'Les sauvegardes de plus de {{days}} jours sont supprimées à cette occasion, comme lors de la sauvegarde automatique.',
    'backups.backupConfirmRetentionUnknown':
      'Les sauvegardes au-delà de la durée de rétention sont supprimées à cette occasion, comme lors de la sauvegarde automatique.',
    'backups.backupConfirmAction': 'Lancer la sauvegarde',
    'backups.backupCancel': 'Annuler',
    'backups.backupLastRunning':
      'Dernière sauvegarde : en cours depuis le {{date}}',
    'backups.backupLastSucceeded': 'Dernière sauvegarde : réussie le {{date}}',
    'backups.backupLastFailed': 'Dernière sauvegarde : en échec le {{date}}',
    'backups.backupAlreadyRunning': 'Une sauvegarde est déjà en cours.',
    'backups.backupBlockedByRestore':
      'Attendez la fin de la restauration en cours avant de lancer une sauvegarde.',
    'backups.backupNotConfigured':
      'Aucune sauvegarde planifiée n’est configurée pour cet environnement.',
    'backups.backupForbidden':
      'Vous n’avez pas le droit de lancer une sauvegarde pour cet environnement.',
    'backups.backupFailedToStart': 'La sauvegarde n’a pas pu être lancée.',
    // L’exclusion mutuelle tenue par le serveur, quand la carte avait une vue
    // périmée : un refus, formulé autrement que les infobulles qui disent
    // « attendez ».
    'backups.backupRefusedRestoreRunning':
      'Une restauration est en cours : la sauvegarde n’a pas été lancée.',
    'backups.restoreRefusedBackupRunning':
      'Une sauvegarde est en cours : la restauration n’a pas été lancée.',
    'backups.restoreBlockedByBackup':
      'Attendez la fin de la sauvegarde en cours avant de restaurer.',
    // Le téléchargement d’un fichier de sauvegarde, depuis le menu de sa
    // ligne. Le bouton n’affiche que le verbe ; son nom accessible nomme la
    // sauvegarde. Chaque entrée dit ce qu’est le fichier et sa taille, le nom
    // du fichier en dessous.
    'backups.downloadMenu': 'Télécharger un fichier de la sauvegarde {{id}}',
    'backups.downloadTooltip': 'Télécharger un fichier',
    'backups.notVisible':
      'Les sauvegardes de cet environnement sont réservées aux éditeurs du projet et aux administrateurs.',
    'backups.pageRange': '{{from}}–{{to}} sur {{total}}',
    'backups.previousPage': 'Sauvegardes précédentes',
    'backups.nextPage': 'Sauvegardes suivantes',
    'backups.fileDatabase': 'Base de données',
    'backups.fileDatabaseNumbered': 'Base de données {{number}}',
    'backups.fileVolume': 'Volume {{number}}',
    'backups.fileWithSize': '{{label}} · {{size}}',
    'backups.downloadFailed': 'Le téléchargement n’a pas pu être préparé.',
    // Calqué sur `backups.restoreForbidden` : une explication, pas un rejet.
    'backups.downloadForbidden':
      'Vous n’avez pas le droit de télécharger une sauvegarde de cet environnement.',
    // « Relancez un inventaire » : le verbe de `backups.rerun`, le bouton qui
    // remet le tableau à jour.
    'backups.downloadMissing':
      'Ce fichier n’est plus dans le bucket. Relancez un inventaire.',
    // La liste lue directement dans le bucket par le backend, ce que la carte
    // affiche chaque fois qu’elle le peut. Aucun workflow n’y prend part : ce
    // n’est donc PAS un inventaire, mot réservé plus haut à l’exécution du
    // workflow `backup-list`. Le bouton relit le bucket — c’est bien une
    // relecture de l’affichage, d’où « Actualiser ». Il affiche
    // `backups.refreshShort` ; `backups.refreshList` est son nom accessible et
    // son infobulle.
    'backups.refreshShort': 'Actualiser',
    'backups.refreshList': 'Actualiser la liste des sauvegardes',
    'backups.listedAt': 'Liste lue à {{time}}',
    'backups.truncated':
      'Le bucket contient plus de fichiers qu’il n’est possible d’en lire en une fois : seule une partie des sauvegardes est listée.',
    // `backups.downloadMissing` quand la liste vient du bucket : on la remet à
    // jour avec le bouton « Actualiser », pas avec un inventaire.
    'backups.downloadMissingRefresh':
      'Ce fichier n’est plus dans le bucket. Actualisez la liste.',
    // Le panneau d’envoi de fichier. « Envoi » désigne partout l’acte de
    // l’utilisateur ; « copie » est réservé à ce que le workflow fait dans le
    // cluster. « Pod » n’est pas traduit — c’est le mot de Kubernetes, celui
    // qu’affichent ArgoCD et le menu des terminaux juste à côté.
    'upload.title': 'Envoyer un fichier',
    'upload.subtitle':
      'Copier un fichier dans un pod en cours d’exécution de cet environnement',
    'upload.notConfigured': 'Aucun envoi configuré pour cet environnement.',
    'upload.featureOff': 'Envoi de fichiers indisponible : {{reason}}',
    'upload.featureOffGeneric':
      'Envoi de fichiers indisponible pour cet environnement (réglage de son type d’application).',
    'upload.podLabel': 'Pod de destination',
    'upload.noPods':
      'Aucun pod en cours d’exécution vers lequel envoyer un fichier.',
    'upload.fileLabel': 'Fichier',
    'upload.targetPathLabel': 'Répertoire de destination',
    // « Un chemin absolu vers un répertoire » et non « un répertoire absolu » :
    // en français, c’est le chemin qui est absolu, pas le répertoire.
    'upload.targetPathInvalid':
      'La destination doit être un chemin absolu vers un répertoire, sans segment « .. » ni caractère spécial du shell.',
    'upload.submit': 'Envoyer',
    // Le libellé que prend le bouton pendant l’envoi, à côté du témoin de
    // chargement de BUI. Il remplace la ligne « Envoi en cours… » qui se tenait
    // à part : un seul indicateur, sur la commande qui vient d’être actionnée.
    'upload.submitting': 'Envoi en cours…',
    'upload.lastRun': 'Dernier envoi : {{date}}',
    // Le registre du constat : ce qui est affiché est le dernier envoi qu’a
    // connu l’ENVIRONNEMENT, pas celui qu’on vient de faire — il peut dater, ou
    // être celui d’un collègue. D’où une seule ligne, datée, sans « votre » ni
    // aucune formule de confirmation, et sans reprendre `upload.lastRun` en
    // dessous : la date est déjà dans la phrase.
    'upload.lastRunHistory': 'Dernier envoi : {{date}}.',
    // « copié » s’accorde avec « fichier », placé en tête comme dans
    // `upload.runSucceededDetails` et pour la même raison : « {{file}} copié »
    // ferait dépendre l’accord d’un nom de fichier inconnu. La phrase reste
    // elliptique — participe sans auxiliaire —, ce qui la tient dans le registre
    // du relevé et non de la confirmation.
    'upload.lastRunHistoryDetails':
      'Dernier envoi : {{date}} — le fichier {{file}} copié dans {{path}} du pod {{pod}}.',
    // « il » reprend « envoi », et non l’exécution du workflow : c’est l’envoi
    // que la phrase vient de nommer.
    'upload.lastRunHistoryFailed': 'Dernier envoi : {{date}} — il a échoué.',
    'upload.noRun': 'Aucun fichier n’a encore été envoyé à cet environnement.',
    'upload.runSucceeded': 'Le dernier fichier envoyé a été copié dans le pod.',
    // « copié » s’accorde avec « fichier », qui ouvre la phrase — c’est aussi
    // pourquoi le nom du fichier n’est pas mis en tête : « {{file}} a été
    // copié » laisserait l’accord dépendre d’un nom de fichier inconnu.
    'upload.runSucceededDetails':
      'Le fichier {{file}} a été copié dans {{path}} du pod {{pod}}.',
    'upload.runFailed': 'Le dernier envoi a échoué.',
    'upload.alreadyRunning':
      'Un envoi est déjà en cours pour cet environnement.',
    // L’infobulle du bouton désactivé : elle dit la règle, au présent, sans
    // reprocher quoi que ce soit à qui lit.
    'upload.submitNotAllowed':
      'L’envoi d’un fichier est réservé aux administrateurs, et aux éditeurs du projet sur dev et ses branches.',
    'upload.submitForbidden':
      'Vous n’avez pas le droit d’envoyer un fichier à cet environnement.',
    'upload.submitFailed': 'Le fichier n’a pas pu être envoyé.',
    'upload.tooLarge':
      'Ce fichier est trop volumineux : la limite est de 2 Gio.',
    // Le code du serveur est laissé tel quel entre parenthèses : la phrase se lit
    // toujours comme une phrase, et le code n’est là que pour retrouver le
    // contrôle qui a refusé. Il ne reste ici que des codes qui signalent un
    // formulaire non conforme à son propre contrat — donc un défaut de notre
    // côté, sur lequel qui lit ne peut rien.
    'upload.rejected': 'Cet envoi a été refusé par le serveur ({{reason}}).',
    // « Rafraîchissez la page » reprend le mot du bouton de l’en-tête
    // (`card.refresh` : « Rafraîchir »), qui relit justement l’arbre des
    // ressources d’où vient la liste des pods. L’instruction et le bouton qui
    // l’exécute doivent se lire avec le même verbe — même règle que
    // `backups.rerun` et `backups.logPruned` plus haut.
    'upload.rejectedNoSuchPod':
      'Ce pod n’existe plus. Rafraîchissez la page pour voir les pods actuels de cet environnement.',
    // « Journal d’exécution » et non « Logs » : le mot « journal » est déjà
    // celui des deux phrases voisines (`upload.logPruned`,
    // `upload.logUnreadable`), et le panneau qui garde « Logs » en anglais plus
    // haut le fait parce qu’il renvoie vers l’onglet LOGS d’ArgoCD — ce qui
    // n’est pas le cas ici.
    'upload.logToggle': 'Journal d’exécution',
    'upload.logPruned':
      'Le journal de cet envoi n’existe plus — Argo Workflows ne conserve que ses vingt dernières exécutions.',
    'upload.logUnreadable': 'Le journal de cet envoi n’a pas pu être lu.',
    'upload.openInArgo': 'Ouvrir cette exécution dans Argo Workflows',
    // Mot pour mot `backups.openNamespaceInArgo` : les deux liens visent la même
    // URL, la liste des exécutions du namespace, et Argo n’y filtre pas les
    // envois. « Les envois de cet environnement » promettrait une liste que la
    // page ouverte ne donne pas, et ferait dire deux choses différentes à deux
    // liens identiques posés l’un sous l’autre.
    'upload.openNamespaceInArgo':
      'Ouvrir les exécutions de cet environnement dans Argo Workflows',
    'upload.retry': 'Réessayer',
    // « L’état des envois » et non « les envois » : ce que la carte n’a pas pu
    // lire est le compte rendu du dernier envoi, pas les fichiers eux-mêmes.
    'upload.unavailable':
      'L’état des envois ne peut pas être lu pour le moment.',
    'nav.projects': 'Projets',
    // Au singulier, comme `card.empty` juste au-dessus le sous-entend : ce que
    // la ligne dit est qu’il n’y en a pas un seul, pas qu’une liste est vide.
    'nav.noBranches': 'Aucun environnement',
    'nav.loadingBranches': 'Chargement…',
    'nav.branchLabel': '{{branch}} ({{description}})',
    'nav.ephemeralBranchLabel':
      '{{branch}}, branche éphémère ({{description}})',
    'nav.ephemeralGroup': 'Éphémères · {{used}}/{{max}}',
    'nav.ephemeralGroupNoMax': 'Éphémères · {{used}}',
    // Deux clés plutôt qu’un pluriel i18next : aucune autre chaîne de ce
    // fichier n’en utilise, et « + 1 autres » ne se dit pas. `hidden` et
    // `used` plutôt que `count`, que le typage de `t` réserve au pluriel.
    'nav.moreEphemeralOne': '+ 1 autre',
    'nav.moreEphemeralOneContext': 'branche éphémère',
    'nav.moreEphemeral': '+ {{hidden}} autres',
    'nav.moreEphemeralContext': 'branches éphémères',
    'nav.lessEphemeral': 'Afficher moins',
    'nav.lessEphemeralContext': 'de branches éphémères',
    'nav.projectBadge': '{{project}}, {{state}}',
    'nav.projectState.success': 'sain',
    'nav.projectState.info': 'déploiement en cours',
    'nav.projectState.warning': 'à surveiller',
    'nav.projectState.danger': 'dégradé',
    'nav.projectState.none': 'aucun environnement en marche',
    'nav.openProject': 'Ouvrir le projet',
    'resources.title': 'Ressources',
    'resources.subtitle':
      'Consommation actuelle, au regard des demandes et des limites des pods',
    'resources.cpu': 'CPU',
    'resources.memory': 'Mémoire',
    'resources.cpuOf': 'CPU de {{branch}}',
    'resources.memoryOf': 'Mémoire de {{branch}}',
    'resources.gaugeLabel': '{{label}} : {{value}} ({{percent}})',
    'resources.gaugeLabelNoBar': '{{label}} : {{value}}',
    'resources.value': '{{used}} / {{limit}} {{unit}}',
    // Espace insécable : « 2,9 Kio » ne se coupe jamais entre le nombre et
    // l’unité.
    'resources.quantity': '{{value}}\u00a0{{unit}}',
    'resources.usageUnavailable': 'Consommation indisponible',
    'resources.details': 'Demandé : {{requested}} · Limite : {{limit}}',
    'resources.requestedMarker': 'Demandé : {{requested}}',
    // Accordé à « limite », le mot qu’il suit dans `resources.details`
    // (« Limite : illimitée »).
    'resources.unbounded': 'illimitée',
    // « vCPU », invariable : ni « 1 cœurs » ni accord à faire, et le même mot
    // que `platform.cpuFigures` de la page d’accueil.
    'resources.unitCores': 'vCPU',
    'resources.unitMiB': 'Mio',
    'resources.unitGiB': 'Gio',
    'resources.volumes': 'Volumes',
    'resources.volumesNote': 'Taille allouée, pas l’espace occupé',
    'resources.volumesNone': 'Aucun volume',
    'resources.volumesTotal': 'Volumes : {{size}}',
    'resources.unavailableShort': 'indisponible',
    'resources.volumeAttachments': 'Pièces jointes',
    'resources.volumeDatabase': 'Base de données',
    'resources.sizeUnknown': 'taille inconnue',
    'resources.grafana': 'Voir les graphiques détaillés dans Grafana',
    'resources.unavailable':
      'Les ressources ne peuvent pas être lues pour le moment.',
    'resources.environmentUnavailable': 'Ressources indisponibles',
    'resources.beyondCap':
      'Non lues : au-delà des {{shown}} premiers environnements du projet',
    'resources.total': 'Total du projet',
    'resources.partial': 'Total des seuls environnements lisibles',
    'resources.totalLine':
      '{{label}} : CPU {{cpu}} · Mémoire {{memory}} · Volumes {{volumes}}',
    'resources.truncated':
      'Consommation des {{shown}} premiers environnements sur {{total}}.',
    'project.refresh': 'Rafraîchir les métadonnées',
    'project.refreshScheduled': 'Rafraîchissement planifié',
    'project.edit': 'Modifier les métadonnées',
    'project.noDescription': 'Aucune description',
    'project.createdBy': 'Créé par',
    'project.createdOn': 'Créé le {{date}}',
    // « 9h42 » : l’heure sans zéro initial, à la française. La ligne se lit
    // « Créé par Dev 1, le 05/10/2026 à 9h42 ».
    'project.createdTime': '{{hour}}h{{minute}}',
    'project.createdOnJoin': ', le',
    'project.createdOnLead': 'Créé le',
    'project.createdAtJoin': 'à',
    'project.tabsLabel': 'Sections de ce projet',
    'project.links': 'Liens',
    // « Tags », as the catalog's About card and tag filter say it in French
    // (@waadoo/backstage-i18n, `aboutCard.tagsField.label`).
    'project.tags': 'Tags',
    'project.newTab': '(nouvel onglet)',
    'project.pipelineRunning': 'En cours · {{branch}}',
    'project.pipelinesRunning_one': 'En cours · {{count}} branche',
    'project.pipelinesRunning_other': 'En cours · {{count}} branches',
    'project.pipelineRunningOn': '— pipeline en cours sur {{branch}}',
    'project.pipelinesRunningOn_one':
      '— pipeline en cours sur {{count}} branche',
    'project.pipelinesRunningOn_other':
      '— pipelines en cours sur {{count}} branches',
    'newBranch.open': 'Nouvelle branche',
    'newBranch.openSubtitle': 'éphémère, depuis {{source}}',
    'newBranch.slotText':
      'créée depuis {{source}} · {{used}}/{{max}} utilisées',
    'newBranch.quotaReached': 'Quota de branches éphémères atteint',
    'newBranch.quotaReachedCount':
      'Quota de branches éphémères atteint ({{used}}/{{max}})',
    'newBranch.menuItem': '+ Nouvelle branche éphémère',
    'newBranch.title': 'Nouvelle branche éphémère',
    'newBranch.quota': '{{used}}/{{max}} utilisées',
    'newBranch.limitReached':
      'Limite de {{max}} branches éphémères atteinte : supprimez-en une avant d’en créer une autre.',
    'newBranch.nameLabel': 'Nom de la branche',
    'newBranch.prefixHint': 'Préfixe {{prefix}} imposé.',
    'newBranch.nameHelp': 'Lettres minuscules, chiffres et tirets.',
    // 0 and 1 take the singular in French (i18next's `one`).
    'newBranch.remaining_one': '{{count}} restant',
    'newBranch.remaining_other': '{{count}} restants',
    'newBranch.problemChars':
      'Seuls les lettres minuscules, les chiffres et les tirets sont acceptés.',
    'newBranch.problemHyphen':
      'Pas de tiret au début ni à la fin, ni deux tirets de suite.',
    'newBranch.problemTooLong':
      'Trop long : {{max}} caractères au plus, préfixe compris.',
    'newBranch.sourceLabel': 'Branche de départ',
    'newBranch.defaultSource': 'code et image actuels',
    'newBranch.planTitle': 'Ce qui va être créé',
    'newBranch.planBranch': 'Branche Git',
    'newBranch.planFrom': 'depuis',
    'newBranch.planEnvironment': 'Environnement {{app}}',
    'newBranch.planDelay': 'Prêt dans 4 à 6 min',
    'newBranch.planDelayDetail':
      'construction de l’image et détection ArgoCD, puis déploiement — suivi en direct sur la page de la branche',
    'newBranch.errorExists': 'La branche {{branch}} existe déjà.',
    'newBranch.errorLimit': 'Limite de {{max}} branches éphémères atteinte.',
    'newBranch.errorSource': 'La branche de départ n’existe plus.',
    'newBranch.errorName': 'Nom refusé par le serveur.',
    'newBranch.errorRefused':
      'Création refusée : droits insuffisants ou projet introuvable.',
    'newBranch.errorUnknown':
      'La création a échoué. Réessayez dans un instant.',
    'newBranch.cancel': 'Annuler',
    'newBranch.submit': 'Créer la branche',
    'deleteBranch.open': 'Supprimer la branche',
    'deleteBranch.title': 'Supprimer la branche {{branch}}',
    'deleteBranch.body':
      'Cette action supprime la branche git {{branch}} puis, dans les minutes qui suivent, son environnement : l’application {{app}}, sa base de données et ses pièces jointes.',
    'deleteBranch.commits':
      'Les commits qui ne sont fusionnés dans aucune autre branche ne seront plus accessibles.',
    'deleteBranch.noBackup':
      'Un environnement éphémère n’a aucune sauvegarde : rien ne pourra être restauré depuis Backstage.',
    'deleteBranch.confirmLabel':
      'Pour confirmer, saisissez le nom de la branche : {{branch}}',
    'deleteBranch.errorProtected':
      'Cette branche est protégée dans GitLab : elle ne peut pas être supprimée d’ici.',
    'deleteBranch.errorNotFound':
      'Branche introuvable, ou droits insuffisants.',
    'deleteBranch.errorUnknown':
      'La suppression a échoué. Réessayez dans un instant.',
    'deleteBranch.cancel': 'Annuler',
    'deleteBranch.submit': 'Supprimer la branche',
    'deleteBranch.inProgress':
      'Suppression de {{branch}} en cours (quelques minutes).',
    'deletion.badge': 'Suppression en cours',
    'deletion.banner':
      'Cette branche est en cours de suppression. L’environnement disparaîtra dans quelques minutes.',
    'deletion.dataUnavailable':
      'Sauvegardes et imports indisponibles pendant la suppression de l’environnement.',
    // « branche » is feminine: « supprimée ».
    'deletion.done': '{{branch}} supprimée',
    'preparing.stepBranch': 'Branche créée',
    'preparing.stepDetection': 'Détection par ArgoCD',
    'preparing.stepDeployment': 'Déploiement',
    'preparing.stepReady': '{{app}} prêt',
    'preparing.statusDone': 'Fait',
    'preparing.statusRunning': 'En cours',
    'preparing.statusFailed': 'Échec',
    'preparing.statusWaiting': 'En attente',
    'preparing.pipelineRun': 'Pipeline {{forge}} #{{id}}',
    'preparing.pipelineFor': 'depuis {{elapsed}}',
    'preparing.pipelineOpen': 'Voir le pipeline',
    'preparing.pipelineLink': 'Pipelines de la branche',
    'preparing.detectionDelay': 'environ 1 min',
    'preparing.deploymentDetail': 'base de données et {{app}}',
    'preparing.polling':
      'Cette page vérifie toutes les 10 secondes et affiche l’environnement dès qu’il apparaît.',
    'preparing.timedOut':
      'Toujours aucun environnement après 15 minutes. Vérifiez le pipeline de la branche et qu’elle contient deploy/Chart.yaml, puis actualisez la page.',
    'service.title': 'Mise en service',
    'service.following': 'Suivi automatique · 10 s',
    'service.image': 'Image {{app}}',
    'service.imageRun': 'pipeline #{{id}}',
    'service.imageEstimate': '≈ 2 à 4 min',
    'service.environment': 'Environnement',
    'service.environmentSteps': 'détection, déploiement, {{app}} prêt',
    'service.deploying': 'Déploiement en cours',
    'service.environmentEstimate': '≈ 2 à 3 min après l’image',
    'service.hintPreparing':
      'La page de la branche s’affiche dès que l’environnement apparaît, et suit son déploiement jusqu’à ce {{thatApp}} soit prêt.',
    'service.hintDeploying':
      'Cette carte suit le déploiement jusqu’à ce {{thatApp}} soit prêt.',
    'service.detailTitle': 'Détail',
    'service.phaseBranch': 'Branche',
    'service.phaseBuild': 'Construction',
    'service.stepGit': 'Branche Git',
    'service.stepDetection': 'Détection ArgoCD',
    'service.buildWaiting': 'aucun pipeline visible pour l’instant',
    'service.readyDetail': 'pods prêts, application saine',
    'service.pillDone': 'Fait · {{duration}}',
    'service.pillRunning': 'En cours · {{duration}}',
    'service.plannedTitle': 'Ce qui va être créé',
    'service.createdTitle': 'Ce qui a été créé',
    'service.planned': 'Prévu',
    'service.created': 'Créés',
    'service.count': '{{items}} élément(s)',
    'service.imageByCi': 'construite par la CI',
    'service.rowDatabase': 'Base PostgreSQL',
    'service.databaseFresh': 'base neuve',
    'service.rowMails': 'Emails',
    'service.mailsCaptured': 'capturés',
    'service.mailsTab': 'onglet Emails',
    'service.rowAddress': 'Adresse',
    'duration.underSecond': '< 1 s',
    'duration.seconds': '{{seconds}} s',
    'duration.minutes': '{{minutes}} min {{seconds}} s',
    'duration.hours': '{{hours}} h {{minutes}} min',
    'ready.title': 'Environnement {{branch}} prêt',
    'ready.titleIn': 'Environnement {{branch}} prêt en {{duration}}',
    'ready.openApp': 'Ouvrir {{app}}',
    'ready.showReport': 'Voir le compte rendu',
    'ready.dismiss': 'Fermer le bandeau',
    'report.title': 'Compte rendu — {{branch}}',
    'report.pipeline': 'Pipeline #{{id}}',
    'report.steps': 'Étapes',
    'report.next': 'Et maintenant ?',
    'report.nextPushLead': 'Poussez sur {{branch}}',
    'report.nextPush':
      '{{lead}} : chaque push reconstruit l’image et redéploie l’environnement (≈ 3 min).',
    'report.nextTestLead': 'Testez',
    'report.nextTest': '{{lead}} sur {{address}}.',
    'report.nextTestMails':
      '{{lead}} sur {{address}} ; les emails envoyés sont dans l’onglet « Emails ».',
    'report.nextTestPlain': '{{lead}} vos changements dans l’environnement.',
    'report.nextMergeLead': 'Fusionnez dans {{target}}',
    'report.nextMergeOriginLead': 'Fusionnez dans la branche d’origine',
    'report.nextMerge':
      '{{lead}} par une merge request ; supprimez ensuite la branche pour libérer sa place.',
    'report.nextMergeQuota':
      '{{lead}} par une merge request ; supprimez ensuite la branche pour libérer sa place ({{used}}/{{max}} utilisées).',
    'copy.label': 'Copier les commandes',
    'copy.done': 'Copié',
    'copy.failed': 'Copie impossible : sélectionnez les commandes à la main',
    'error.notJson':
      'Réponse inattendue de l’API ArgoCD (ce n’est pas du JSON) : {{body}}',
    'error.noIdentity':
      'Vous n’êtes pas connecté via Authelia. Le mode invité ne peut pas interroger ArgoCD.',
    'readOnly.label': 'Lecture seule',
    'readOnly.tooltip':
      'Lecture seule : vous êtes membre de ce projet, sans droits d’éditeur (pas de terminal, de création ou suppression de branche, de relance de déploiement ni de restauration).',
    'readOnly.headerLabel': 'Accès',
    'appVersion.headerLabel': 'Version',
    'appVersion.label': '{{app}} {{version}}',
    'readOnly.navProject': '{{project}}, lecture seule',
    'events.title': 'À surveiller',
    'events.subtitle': 'Avertissements Kubernetes des 6 dernières heures',
    'events.listLabel': 'Avertissements Kubernetes récents',
    'events.level.serious': 'À traiter',
    'events.level.transient': 'Passager',
    'events.collapsedSummary_one':
      '{{count}} avertissement · il y a {{age}} : {{label}} · {{object}}',
    'events.collapsedSummary_other':
      '{{count}} avertissements · dernier il y a {{age}} : {{label}} · {{object}}',
    'events.category.oom': 'Mémoire épuisée',
    'events.category.crashLoop': 'Redémarrages en boucle',
    'events.category.probe': 'Sonde en échec',
    'events.category.image': 'Image introuvable',
    'events.category.scheduling': 'En attente de placement',
    'events.category.volume': 'Volume',
    'events.category.job': 'Job en échec',
    'events.category.evicted': 'Pod évincé',
    'events.platform.observe.ovhApi':
      'Vérification reportée — API OVH indisponible un instant',
    'events.platform.observe.other': 'Vérification en échec',
    'events.platform.create.ovhApi':
      'Création en échec — API OVH indisponible un instant',
    'events.platform.create.other': 'Création en échec',
    'events.platform.update.ovhApi':
      'Mise à jour en échec — API OVH indisponible un instant',
    'events.platform.update.other': 'Mise à jour en échec',
    'events.platform.delete.ovhApi':
      'Suppression en échec — API OVH indisponible un instant',
    'events.platform.delete.other': 'Suppression en échec',
    'events.platform.generic': 'Plateforme',
    'events.categoryOther': '{{reason}}',
    'events.count': '×{{times}}',
    'events.countLabel': '{{times}} occurrences',
    'events.countLabelOne': '1 occurrence',
    'events.lastSeen': 'il y a {{age}}',
    'events.seenTitle':
      'Dernière occurrence le {{last}} · première le {{first}}',
    'events.reasonTitle': 'Raison Kubernetes : {{reason}}',
    'code.label': 'Code de la branche',
    'code.lastCommit': 'Dernier commit',
    'code.committedAgo': 'il y a {{age}}',
    'code.openCommit': 'Ouvrir le commit {{sha}} dans GitLab',
    'code.commitTitle': 'Commit du {{date}}',
    'code.ahead': '{{n}} en avance',
    'code.behind': '{{n}} en retard sur {{base}}',
    'code.divergenceTitle': 'Commits par rapport à {{base}}',
    'code.mrOpened': 'MR !{{iid}} ouverte',
    'code.mrMerged': 'MR !{{iid}} fusionnée',
    'code.mrDraft': 'brouillon',
    'code.openMr': 'Ouvrir la merge request !{{iid}} dans GitLab : {{title}}',
    'code.mrPipeline': 'Pipeline de la merge request',
    'code.noMr': 'Aucune merge request',
    'code.createMr': 'Créer une merge request',
    'initReport.jobLog': 'Voir le log du job',
    'syncFailure.retrying':
      'Le déploiement échoue : nouvelle tentative en cours ({{progress}}) sur le commit {{commit}}. Un commit correctif est pris en compte à la prochaine tentative.',
    'syncFailure.failed_one':
      'Le déploiement du commit {{commit}} a échoué après {{count}} nouvelle tentative.',
    'syncFailure.failed_other':
      'Le déploiement du commit {{commit}} a échoué après {{count}} nouvelles tentatives.',
    'syncFailure.failedOnce': 'Le déploiement du commit {{commit}} a échoué.',
    'syncFailure.unknownCommit': 'inconnu',
    'syncFailure.resource': '{{kind}} {{name}} : {{message}}',
    'syncFailure.resourceNoMessage': '{{kind}} {{name}} en échec.',
    'syncFailure.restart': 'Relancer sur le dernier commit',
    'syncFailure.restarted': 'Déploiement relancé sur le dernier commit.',
    'syncFailure.error.forbidden':
      'Vous n’avez pas le droit de relancer le déploiement de cet environnement.',
    'syncFailure.error.notFound':
      'L’application ArgoCD de cet environnement est introuvable.',
    'syncFailure.error.busy':
      'Une synchronisation est toujours en cours. Réessayez dans un instant.',
    'syncFailure.error.failed':
      'Le déploiement n’a pas pu être relancé. Réessayez dans un instant.',
    'promotion.title': 'Promotion',
    'promotion.subtitle': '{{chain}}, par merge request',
    'promotion.chainLabel': 'Chaîne de promotion',
    'promotion.gap_one': '+{{count}} commit',
    'promotion.gap_other': '+{{count}} commits',
    'promotion.gapNone': 'à jour',
    'promotion.deployed': 'Déployé',
    'promotion.neverDeployed': 'jamais déployé',
    'promotion.noBranch': 'branche absente du dépôt',
    'promotion.deployedAgo': 'déployé {{ago}}',
    'promotion.nextTitle':
      'Prochaine étape : promouvoir {{source}} vers {{target}}',
    'promotion.nextCommits_one':
      '{{count}} commit testé sur {{source}}, image {{image}}.',
    'promotion.nextCommits_other':
      '{{count}} commits testés sur {{source}}, image {{image}}.',
    'promotion.nextCommitsNoImage_one':
      '{{count}} commit testé sur {{source}}.',
    'promotion.nextCommitsNoImage_other':
      '{{count}} commits testés sur {{source}}.',
    'promotion.nextAsleep':
      '{{target}} est en sommeil : la version y sera déployée à son prochain réveil.',
    'promotion.nextAdmins':
      'Cette promotion est faite par les administrateurs.',
    'promotion.upToDateTitle': 'Tout est à jour',
    'promotion.upToDateDetail':
      'Chaque environnement de la chaîne a les commits du précédent.',
    'promotion.openMergeRequest': 'Merge request déjà ouverte',
    'promotion.openMergeRequestPair':
      'Merge request {{source}} → {{target}} déjà ouverte',
    'promotion.commitsTitle': 'Commits à promouvoir · {{total}}',
    'promotion.showAll_one': 'Voir le commit',
    'promotion.showAll_other': 'Voir les {{count}} commits',
    'promotion.showFewer': 'Voir moins',
    'promotion.moreCommits_one': 'et {{count}} autre',
    'promotion.moreCommits_other': 'et {{count}} autres',
    'promotion.image': 'Image',
    'promotion.imageBuilt':
      'construite et testée sur {{source}} · reprise telle quelle par {{target}}, sans reconstruction',
    'promotion.imageReplaces': 'remplace {{image}}',
    'promotion.sourceNoImage': '{{source}} n’a pas encore d’image',
    'promotion.targetNoImage': '{{target}} n’a pas encore d’image',
    'promotion.ciUnchanged': 'CI inchangée',
    'promotion.otherActions': 'Autres actions :',
    'promotion.ruleAdmins': '{{target}} : promue par les administrateurs',
    'promotion.button': 'Promouvoir {{source}} → {{target}}',
    'promotion.branchButton': 'Promouvoir vers {{target}}',
    'promotion.ciLocked': 'Modifie la CI : réservé aux administrateurs',
    'promotion.oldCiLocked':
      'La CI du projet construit encore sur cette branche : réservé aux administrateurs (migrer la CI)',
    'promotion.footerReadOnly':
      'Les promotions sont faites par les éditeurs du projet et les administrateurs.',
    'promotion.pillPromote_one':
      '{{count}} commit à promouvoir vers {{target}}',
    'promotion.pillPromote_other':
      '{{count}} commits à promouvoir vers {{target}}',
    'promotion.pillAdmins_one':
      '{{count}} commit à promouvoir vers {{target}} par les administrateurs',
    'promotion.pillAdmins_other':
      '{{count}} commits à promouvoir vers {{target}} par les administrateurs',
    'promotion.pillUpToDate': 'Toutes les branches sont à jour',
    'promotion.showDetails': 'Afficher le détail',
    'promotion.hideDetails': 'Masquer le détail',
    'promote.title': 'Promouvoir {{source}} → {{target}}',
    'promote.subtitleEditors': '{{project}} · éditeurs et administrateurs',
    'promote.subtitleAdmins': '{{project}} · administrateurs',
    'promote.commitsTitle_one': '{{count}} commit arrive sur {{target}}',
    'promote.commitsTitle_other': '{{count}} commits arrivent sur {{target}}',
    'promote.moreCommits': '… et {{more}} de plus',
    'promote.changesTitle': 'Ce qui change sur {{target}}',
    'promote.changeCode': 'le code et les modules de ces commits',
    'promote.changeCodeStandard': 'la version et les réglages de ces commits',
    'promote.changeImage':
      'l’image : celle de {{source}}, déjà construite et testée, reprise telle quelle ({{target}} ne construit jamais)',
    'promote.changeCommon':
      'les réglages communs (deploy/values.yaml) s’ils ont changé',
    'promote.keptTitle': 'Ce qui ne change pas',
    'promote.keptCapacity':
      'capacité : {{component}} {{cpu}} CPU, {{memory}} de mémoire',
    'promote.keptHpa': 'HPA {{min}} → {{max}} pods',
    'promote.keptComponentHpa': '{{component}} : HPA {{min}} → {{max}} pods',
    'promote.keptPods_one': '{{component}} : {{count}} pod (sans autoscaling)',
    'promote.keptPods_other':
      '{{component}} : {{count}} pods (sans autoscaling)',
    'promote.keptNoAutoscaling': '{{component}} : sans autoscaling',
    'promote.keptDisabled': '{{component}} : désactivé',
    'promote.keptEnabled': '{{component}} : activé',
    'promote.keptVolumes': 'volumes : {{volumes}}',
    'promote.keptVolume': '{{label}} {{size}}',
    'promote.volumeFiles': 'fichiers',
    'promote.volumeDatabase': 'base',
    'promote.keptDatabaseInstances': 'PostgreSQL : {{instances}} instance(s)',
    'promote.keptSetting': '{{label}} : {{value}}',
    'promote.valueYes': 'oui',
    'promote.valueNo': 'non',
    'promote.keptGeneric':
      'la configuration de {{target}} : capacité, volumes, haute disponibilité ({{target}}/values.yaml de l’appenv)',
    'promote.keptData': 'les données de {{target}} : base et fichiers',
    'promote.keptDataDatabase': 'les données de {{target}} : base',
    'promote.keptDataFiles': 'les données de {{target}} : fichiers',
    'promote.keptDataGeneric': 'les données de {{target}}',
    'promote.how':
      'Backstage ouvre (ou reprend) la merge request {{source}} → {{target}} et la fusionne ; la CI de {{target}} reprend l’image déjà construite et testée sur {{source}}, puis {{target}} est redéployé. Suivi dans « Tâches ».',
    'promote.howSleeping':
      'Backstage ouvre (ou reprend) la merge request {{source}} → {{target}} et la fusionne ; la CI de {{target}} reprend l’image déjà construite et testée sur {{source}}. {{target}} est en sommeil : il sera déployé à son prochain réveil. Suivi dans « Tâches ».',
    'promote.howStandard':
      'Backstage vérifie que {{source}} est déployé à sa dernière révision, ouvre (ou reprend) la merge request {{source}} → {{target}} et la fusionne ; {{target}} est ensuite redéployé à cette révision, sans image à construire ni à reprendre. Suivi dans « Tâches ».',
    'promote.howSleepingStandard':
      'Backstage vérifie que {{source}} est déployé à sa dernière révision, ouvre (ou reprend) la merge request {{source}} → {{target}} et la fusionne, sans image à construire ni à reprendre. {{target}} est en sommeil : il sera déployé à son prochain réveil. Suivi dans « Tâches ».',
    'promote.ciWarning':
      'Cette promotion modifie la CI ({{files}}) : relire ces fichiers avant de promouvoir.',
    'promote.oldCiWarning':
      'La CI de {{target}} construit encore ({{jobs}}) : le code promu sera exécuté avec le jeton de Backstage. Migrer la CI du projet.',
    'promote.viewMergeRequest': 'Voir la merge request',
    'promote.submit': 'Promouvoir',
    'configuration.unavailable':
      'La configuration de cet environnement n’est pas disponible ici.',
    'configuration.failed': 'La configuration n’a pas pu être lue.',
    'configuration.unreadableTitle': 'Des valeurs de {{files}} sont illisibles',
    'configuration.unreadableDescription':
      'Rien n’est modifiable d’ici tant qu’un administrateur n’a pas corrigé le fichier.',
    'configuration.lockReadOnly':
      'Lecture seule : la configuration est modifiée par les éditeurs du projet et les administrateurs.',
    'configuration.lockProduction':
      'La configuration de prod est modifiée par les administrateurs.',
    'configuration.sleeping':
      '{{branch}} est en sommeil : une modification est enregistrée maintenant et appliquée à son prochain réveil.',
    'configuration.tierEditors': 'éditeurs',
    'configuration.tierAdmins': 'administrateurs',
    'configuration.tierRead': 'lecture seule',
    'configuration.panelLabel': 'Quota, comparaison et modifications',
    'configuration.capacityTitle': 'Capacité',
    'configuration.differences_one': '{{count}} écart avec {{branch}}',
    'configuration.differences_other': '{{count}} écarts avec {{branch}}',
    'configuration.noDifference': 'Aucun écart avec {{branch}}',
    'configuration.changesTitle': 'Modifications · {{changes}}',
    'configuration.environments': 'Environnements',
    'configuration.environmentsHint':
      'Ouvre la configuration de cette branche.',
    'configuration.environmentLocked': 'lecture seule pour vous',
    'configuration.compareWith': 'Comparer avec',
    'configuration.compareNone': 'aucun',
    'configuration.reference': '{{branch}} : {{value}}',
    'configuration.quotaTitle': 'Quota de l’environnement',
    'configuration.quotaCpuShort': 'CPU réservé',
    'configuration.quotaMemoryShort': 'Mémoire réservée',
    'configuration.quotaCpu': 'Quota CPU réservé',
    'configuration.quotaMemory': 'Quota mémoire réservée',
    'configuration.quotaPods': 'Pods',
    'configuration.quotaAsleep':
      'Pas de quota en cours : {{branch}} est en sommeil, sans namespace.',
    'configuration.quotaMissing':
      'Aucun quota lu pour le namespace de cet environnement.',
    'configuration.instances': 'Instances',
    'configuration.ha': 'Haute disponibilité',
    'configuration.profile': 'Profil',
    'configuration.dbCpu': 'PostgreSQL — CPU réservé / plafond',
    'configuration.dbMemory': 'PostgreSQL — mémoire réservée / plafond',
    'configuration.perPod': 'par pod',
    'configuration.perInstance': 'par instance',
    'configuration.was': 'était {{value}}',
    'configuration.growOnly': 'agrandissement seulement',
    'configuration.pods': 'Pods minimum / maximum',
    'configuration.instances1': '1',
    'configuration.instances2': '2 inst.',
    'configuration.instances3': '3 inst.',
    'configuration.modeSingle': 'Instance unique',
    'configuration.modeHa2': 'HA — 2 instances, deux zones (stricte)',
    'configuration.modeHa3': 'HA — 3 instances, une par zone (stricte)',
    'configuration.syncSingle': 'sans objet avec une instance',
    'configuration.yes': 'oui',
    'configuration.no': 'non',
    'configuration.appliedAtWake_one': 'appliquée au réveil',
    'configuration.appliedAtWake_other': 'appliquées au réveil',
    'configuration.noChanges': 'Aucune modification pour l’instant.',
    'configuration.derived': 'déduit',
    'configuration.peak': 'Au plus fort : {{cpu}} · {{memory}} réservés',
    'configuration.margin': 'marge du cluster : {{cpu}} · {{memory}}',
    'configuration.note':
      'Commit dans {{repo}} · {{file}}, puis redéploiement de {{branch}} ({{restart}}). Le code n’est pas touché.',
    'configuration.noteSleeping':
      'Commit dans {{repo}} · {{file}} ; {{branch}} est en sommeil et la prendra à son prochain réveil. Le code n’est pas touché.',
    'configuration.cancel': 'Annuler',
    'configuration.apply': 'Appliquer',
    'configuration.problemLocked':
      '{{field}} : la valeur actuelle est hors des bornes du formulaire de création, réservée aux administrateurs.',
    'configuration.problemBounds':
      '{{field}} : de {{min}} à {{max}}, par pas de {{step}}.',
    'configuration.problemBeyondEditors': 'Au-delà : administrateurs.',
    'configuration.problemShrink':
      '{{field}} : un volume ne peut qu’être agrandi ({{current}} Gio actuellement).',
    'configuration.problemRequestAboveLimit':
      '{{field}} : au-dessus de son plafond, Kubernetes refuserait le pod.',
    'configuration.problemMaxBelowMin':
      'Le nombre maximum de pods doit être au moins égal au minimum.',
    'configuration.problemSharedBuffers':
      'La mémoire réservée doit couvrir le quart du plafond (shared_buffers = {{buffers}} Mio), sinon CloudNativePG refuse la base.',
    'configuration.capacityDescriptionGeneric':
      'Profil de chaque pod de l’application {{app}} ; les valeurs fines ci-dessous.',
    'configuration.lockEphemeralGeneric':
      'Configuration effective de cette branche éphémère : les ressources de dev, allégées pour les branches éphémères. Lecture seule.',
    'configuration.noteGeneric':
      'Commit dans {{repo}} · {{file}}, puis redéploiement de {{branch}}. Le code n’est pas touché.',
    'configuration.componentCpu': '{{component}} — CPU réservé / plafond',
    'configuration.componentMemory':
      '{{component}} — mémoire réservée / plafond',
    'configuration.componentPods': '{{component}} — pods minimum / maximum',
    'configuration.podCount_one': '{{count}} pod',
    'configuration.podCount_other': '{{count}} pods',
    'configuration.podRange': '{{min}} → {{max}} pods',
    'configuration.componentDisabled': 'désactivé',
    'configuration.groupTitle': '{{group}} ({{members}})',
    'configuration.groupComponent': 'Composant',
    'configuration.groupPods': 'Pods',
    'configuration.groupCpu': 'CPU',
    'configuration.groupMemory': 'Mémoire',
    'configuration.volumesSection': 'Volumes',
    'configuration.usedBy': 'utilisé par : {{components}}',
    'configuration.mariadbNote': 'Une instance, sans haute disponibilité.',
    'configuration.othersSection': 'Autres réglages',
    'configuration.adjusted': 'imposé par les règles du type',
    'configuration.problemValues': '{{field}} : valeurs permises {{allowed}}.',
    // La carte d’accès (CredentialsCard.tsx), seule dans l’onglet Accès d’un
    // type qui déclare la fonction `credentials` : les identifiants
    // administrateur de l’environnement, lus dans le secret Kubernetes où son
    // chart les garde (venus du module Odoo, plan « généricité », étape 7 ;
    // `{{app}}` est le titre du type). « Afficher » et non « Révéler » : c’est le mot des
    // champs de mot de passe du navigateur, donc un mot qui n’a pas à
    // s’apprendre.
    'credentials.title': 'Accès à {{app}}',
    'credentials.subtitle': 'Identifiants administrateur de cet environnement',
    'credentials.login': 'Identifiant',
    'credentials.password': 'Mot de passe',
    'credentials.reveal': 'Afficher',
    'credentials.hide': 'Masquer',
    'credentials.copy': 'Copier',
    'credentials.copied': 'Copié',
    // Le secret est nommé plutôt que désigné par « un secret Kubernetes » :
    // l’endroit où vit le mot de passe est le propos de la carte, et le nom est
    // ce qui le rend vérifiable au `kubectl` par qui le souhaite.
    'credentials.source':
      'Généré avec l’environnement et conservé dans le secret Kubernetes {{secret}}.',
    // Un constat, pas un échec — le registre de `backups.notConfigured` : rien
    // n’est cassé, l’environnement n’a simplement pas encore été initialisé.
    'credentials.notConfigured':
      'Aucun identifiant administrateur pour cet environnement. Ils sont générés à sa première initialisation.',
    'credentials.unavailable':
      'Les identifiants ne peuvent pas être lus pour le moment.',
    'credentials.retry': 'Réessayer',
    // Le presse-papiers est indisponible hors contexte sécurisé et peut être
    // refusé ; la phrase doit dire quoi faire à la place, sans quoi le bouton
    // se lit comme cassé.
    'credentials.copyFailed':
      'Le navigateur a refusé la copie : affichez le mot de passe et sélectionnez-le.',
    // Seulement pour un type qui déclare `credentials.loginPath` : aucun pour
    // Odoo.
    'credentials.loginPage': 'Ouvrir la page de connexion',
    // À la place des identifiants quand la page sait que la route les
    // refuserait (règle du 2026-10-29, `credentialsAccess`) : un simple membre
    // du projet, un éditeur sur la production. Le registre de
    // `backups.notVisible` : qui le peut, pas une erreur.
    'credentials.editorsOnly': 'Identifiants réservés aux éditeurs du projet.',
    'credentials.adminsOnly':
      'Identifiants de production réservés aux administrateurs de la plateforme.',
    // La restauration à un instant (plan Plateforme-backup-restore, étape 8,
    // lot 3). Toutes les heures sont celles de Paris, et le disent.
    'pitr.title': 'Restauration à un instant',
    'pitr.subtitle': 'Base et médias ramenés ensemble à la seconde près',
    'pitr.subtitleDatabaseOnly': 'Base ramenée à la seconde près',
    'pitr.unavailableRead': 'La restauration à un instant n’a pas pu être lue.',
    'pitr.retry': 'Réessayer',
    'pitr.archivingOk': 'Journaux archivés depuis le {{since}}',
    'pitr.archivingOkNoDate': 'Journaux archivés',
    'pitr.archivingKo': 'Archivage des journaux en échec depuis le {{since}}',
    'pitr.archivingKoNoDate': 'Archivage des journaux en échec',
    'pitr.windowLine':
      'Base restaurable à la seconde près du {{from}} à maintenant (heure de Paris).',
    'pitr.shortcutsLabel': 'Revenir…',
    'pitr.shortcutDeploy': 'Juste avant le dernier déploiement · {{date}}',
    'pitr.shortcutConfig': 'Juste avant la dernière configuration · {{date}}',
    'pitr.shortcutHour': 'Il y a 1 heure',
    'pitr.shortcutOther': 'Autre heure…',
    'pitr.daysLabel':
      '… ou choisir un jour (restaurable du {{from}} à maintenant, heure de Paris)',
    'pitr.daySnapshots_one': '{{count}} instantané',
    'pitr.daySnapshots_other': '{{count}} instantanés',
    'pitr.dayBarman_one': '{{count}} sauvegarde complète',
    'pitr.dayBarman_other': '{{count}} sauvegardes complètes',
    'pitr.dayDeploys_one': '{{count}} déploiement',
    'pitr.dayDeploys_other': '{{count}} déploiements',
    'pitr.dayConfigs_one': '{{count}} changement de configuration',
    'pitr.dayConfigs_other': '{{count}} changements de configuration',
    'pitr.dayNothing': 'rien de noté',
    'pitr.dayToday': 'en cours',
    'pitr.eventsUnavailable':
      'Historique GitLab indisponible : les déploiements et changements de configuration ne sont pas affichés.',
    'pitr.forbidden':
      'Réservé aux administrateurs (et aux éditeurs du projet sur dev).',
    'pitr.reasonNoTemplates':
      'La restauration à un instant n’est pas encore installée pour cet environnement.',
    'pitr.reasonNoCluster':
      'Aucune base PostgreSQL active n’a été trouvée pour cet environnement.',
    'pitr.reasonNoArchiving':
      'L’archivage continu des journaux de la base n’est pas actif : aucun instant n’est restaurable.',
    'pitr.reasonFilesOnVolume':
      'Les pièces jointes sont sur un volume : la restauration à un instant sera disponible quand elles seront sur S3.',
    'pitr.reasonPending':
      'Une restauration attend sa validation : validez-la ou annulez-la d’abord.',
    'pitr.unavailableButton': 'Restaurer à un instant…',
    'pitr.pendingChip': 'en attente de validation',
    'pitr.pendingInfo':
      'Une autre restauration sera possible après validation ou annulation.',
    'pitr.historyToggle': 'Historique des restaurations',
    'pitr.historyEmpty':
      'Aucune restauration parmi les {{limit}} tâches les plus récentes.',
    'pitr.historyFailed': 'L’historique n’a pas pu être lu.',
    'pitr.historyNote':
      'Parmi les {{limit}} tâches les plus récentes que vous pouvez voir.',
    'pitr.historyDate': 'Date',
    'pitr.historyOperation': 'Opération',
    'pitr.historyStatus': 'Résultat',
    'pitr.historyTask': 'Tâche',
    'pitr.historyTaskLink': 'tâche',
    'pitr.historyRestore': 'Retour au {{date}}',
    'pitr.historyRestoreNoDate': 'Restauration',
    'pitr.historyValidate': 'Validation',
    'pitr.historyCancel': 'Annulation',
    'pitr.statusRunning': 'en cours',
    'pitr.statusCompleted': 'terminée',
    'pitr.statusFailed': 'échec',
    'pitr.statusCancelled': 'annulée',
    'pitr.headerBadge': 'restauration à valider',
    'pitr.bannerTitle': 'Restauré au {{date}} (heure de Paris) — à valider.',
    'pitr.bannerTitleNoDate': 'Restauration à valider.',
    'pitr.bannerText':
      'Le site tourne sur la base restaurée. L’ancienne base est gardée : « Annuler » y revient, avec les médias d’avant la restauration. « Valider » la supprime, ainsi que ses instantanés.',
    'pitr.bannerOverview':
      'Restauré au {{date}} (heure de Paris) — à valider dans l’onglet Données.',
    'pitr.bannerOverviewNoDate':
      'Une restauration est à valider dans l’onglet Données.',
    'pitr.showData': 'Voir dans Données',
    'pitr.cancelRestore': 'Annuler la restauration',
    'pitr.validate': 'Valider',
    'pitr.validateTitle': 'Valider la restauration de {{branch}}',
    'pitr.validateText':
      'L’ancienne base et ses instantanés seront supprimés : la restauration ne pourra plus être annulée.',
    'pitr.validateSubmit': 'Valider la restauration',
    'pitr.cancelTitle': 'Annuler la restauration de {{branch}}',
    'pitr.cancelText':
      'Le site sera arrêté, les médias ramenés à leur état d’avant la restauration et l’ancienne base remise en service, puis le site redémarré. Ce qui a été écrit depuis la restauration sera perdu.',
    'pitr.back': 'Retour',
    'pitr.dayTitle': 'Restaurer {{branch}} à un instant',
    'pitr.daySubtitle': '{{project}} · heure de Paris',
    'pitr.previousDay': 'Jour précédent, {{day}}',
    'pitr.nextDay': 'Jour suivant, {{day}}',
    'pitr.hourLabel': 'Heure',
    'pitr.hourCell': '{{hour}} h',
    'pitr.hourCellRepeat': '{{hour}} h, deuxième fois (heure d’hiver)',
    'pitr.markerSnapshot': 'Instantané',
    'pitr.markerBarman': 'Sauvegarde complète',
    'pitr.legend':
      '◇ instantané · ● sauvegarde complète · ■ configuration · ▲ déploiement · ⇢ bascule de base · pâle : partiellement restaurable · atténué : sera défait · hachuré : non restaurable',
    'pitr.exactTime': 'Heure exacte (HH:MM:SS, Paris)',
    'pitr.minuteSlider': 'Minute de {{hour}} h',
    'pitr.timeInvalid': 'Saisissez une heure au format HH:MM:SS.',
    'pitr.timeMissing':
      'Cette heure n’existe pas ce jour-là (passage à l’heure d’été).',
    'pitr.timeOutside': 'En dehors de la fenêtre restaurable.',
    'pitr.eventDeploy': '{{time}} déploiement {{label}}',
    'pitr.eventConfig': '{{time}} configuration',
    'pitr.eventConfigBy': '{{time}} configuration ({{by}})',
    'pitr.planLoading': 'Vérification de cet instant…',
    'pitr.planFailed': 'L’aperçu n’a pas pu être lu.',
    'pitr.planDatabase': 'Base',
    'pitr.planZone': 'zone {{zone}}',
    'pitr.planMedia': 'Médias',
    'pitr.planMediaAt': 'ramenés au {{date}}',
    'pitr.planNoBucket': 'Aucun bucket de données : base seule.',
    'pitr.planSite': 'Site',
    'pitr.refusalInvalid': 'Cet instant n’est pas valide.',
    'pitr.refusalFuture': 'Cet instant est dans le futur.',
    'pitr.refusalBeforeWindow': 'Avant le début de la fenêtre restaurable.',
    'pitr.refusalNoSource':
      'Aucune sauvegarde de la base avant cet instant : choisissez un instant plus tardif.',
    'pitr.refusalNotFound': 'Cet environnement est introuvable.',
    'pitr.refusalUnavailable':
      'La restauration à un instant est indisponible pour cet environnement.',
    'pitr.utc': 'soit {{utc}}',
    'pitr.simulate': 'Simuler le bucket',
    'pitr.simulating': 'Simulation en cours…',
    'pitr.simulationSummary':
      'Simulation : {{hide}} à masquer, {{restore}} à restaurer, {{keep}} inchangés.',
    'pitr.simulationErrors': 'Erreurs : {{errors}}.',
    'pitr.simulationFailed': 'La simulation a échoué.',
    'pitr.simulationTooRecent':
      'Trop récent pour simuler : choisissez un instant d’au moins une minute dans le passé.',
    'pitr.simulationRunning':
      'Une simulation est déjà en cours pour cet environnement.',
    'pitr.simulationUnavailable':
      'Simulation indisponible pour cet environnement.',
    'pitr.close': 'Fermer',
    'pitr.confirmTitle': 'Confirmer la restauration de {{branch}} au {{date}}',
    'pitr.confirmSubtitle': '{{utc}} · heure de Paris',
    'pitr.confirmStop':
      'Le site de {{branch}} sera arrêté pendant l’opération, puis redémarré.',
    'pitr.confirmMedia':
      'Les médias seront ramenés au {{date}} ; les fichiers ajoutés depuis seront masqués (récupérables).',
    'pitr.confirmDatabaseOnly':
      'Seule la base est ramenée : les fichiers ne le sont pas.',
    'pitr.confirmDecide':
      'Je validerai ou annulerai ensuite : l’ancienne base est gardée jusque-là.',
    'pitr.confirmSubmit': 'Restaurer pour de bon',
    // Les générations antérieures (plan Plateforme-pitr-b1-a2, étape 5) :
    // la fenêtre couvre la rétention, par-delà les restaurations validées.
    'pitr.dayNotRestorable': 'Aucun instant de ce jour n’est restaurable.',
    // Les cartes de jour en 24 h (maquette « v2-jours », 2026-11-03).
    'pitr.daySwitchFirst': 'bascule vers g{{generation}} à {{time}}',
    'pitr.daySwitchNext': 'vers g{{generation}} à {{time}}',
    'pitr.and': 'et',
    'pitr.bandCurrent': 'g{{generation}} · en service',
    'pitr.bandUnknown': '?',
    'pitr.lostCallout':
      'Tout ce qui a été fait après le {{day}} à {{time}} sera perdu',
    'pitr.badgeSnapshot': 'instantané',
    'pitr.badgeOlder': 'ancienne base g{{generation}}',
    'pitr.badgeFull': 'sauvegarde complète',
    'pitr.badgeBucket': 'bucket S3',
    'pitr.badgeSite': 'arrêt pendant l’opération',
    'pitr.planBaseSnapshot':
      'instantané du {{date}} + journaux jusqu’à {{time}}',
    'pitr.planBaseFull':
      'sauvegarde complète du {{date}} + journaux jusqu’à {{time}}',
    'pitr.planBaseOlder':
      'sauvegarde complète du {{date}} au plus tôt + journaux jusqu’à {{time}} · restauration plus longue',
    'pitr.planBaseNext':
      'nouvelle base g{{next}}, g{{current}} gardée jusqu’à la validation',
    'pitr.planSiteStop': 'arrêté pendant l’opération, puis redémarré',
    'pitr.planSiteKept': 'image et configuration actuelles conservées',
    'pitr.periodsHide': 'Masquer le détail',
    'pitr.dayNav': 'Jour',
    'pitr.bandLost': 'g{{generation}} ✕',
    'pitr.bandLostLong': 'g{{generation}}, non restaurable : {{reason}}',
    'pitr.bandUnknownLong': 'base inconnue',
    'pitr.legendFull': 'Légende complète',
    'pitr.chipSnapshot': 'instantané',
    'pitr.chipBarman': 'sauvegarde complète',
    'pitr.chipConfig': 'configuration',
    'pitr.chipDeploy': 'déploiement',
    'pitr.stepMinusHour': '−1 h',
    'pitr.stepMinusMinute': '−1 min',
    'pitr.stepPlusMinute': '+1 min',
    'pitr.stepPlusHour': '+1 h',
    'pitr.stepMinusHourName': 'Une heure plus tôt',
    'pitr.stepMinusMinuteName': 'Une minute plus tôt',
    'pitr.stepPlusMinuteName': 'Une minute plus tard',
    'pitr.stepPlusHourName': 'Une heure plus tard',
    'pitr.justBefore': 'Juste avant :',
    'pitr.beforeSnapshot': 'instantané de {{time}}',
    'pitr.beforeBarman': 'sauvegarde complète de {{time}}',
    'pitr.beforeConfig': 'configuration de {{time}}',
    'pitr.beforeDeploy': 'déploiement de {{time}}',
    'pitr.simulateInline': 'simuler',
    'pitr.simulateHint': 'pour voir ce qui serait masqué',
    'pitr.restoreAt': 'Restaurer à {{time}}…',
    'pitr.tick0': '0 h',
    'pitr.tick12': '12 h',
    'pitr.tick24': '24 h',
    'pitr.dayGaps': 'non restaurable {{ranges}}',
    'pitr.dayGapRange': 'de {{from}} à {{to}}',
    'pitr.dayLabelGaps': '{{main}} ; {{gaps}}',
    'pitr.periodsTitle_one':
      '{{count}} période non restaurable dans la rétention : {{periods}}.',
    'pitr.periodsTitle_other':
      '{{count}} périodes non restaurables dans la rétention : {{periods}}.',
    'pitr.periodsWhy': 'Pourquoi ?',
    'pitr.periodsPanelTitle_one': '{{count}} période non restaurable',
    'pitr.periodsPanelTitle_other': '{{count}} périodes non restaurables',
    'pitr.periodEndUnsure':
      'fin de g{{generation}} non garantie (journaux juste avant sa bascule)',
    'pitr.periodNoBarmanYet':
      'g{{generation}} : pas encore de sauvegarde complète',
    'pitr.periodNoBarman': 'g{{generation}} : aucune sauvegarde complète',
    'pitr.periodSwitch': 'g{{generation}} : bascule, pas encore sauvegardée',
    'pitr.periodNoBackup': 'g{{generation}} : pas encore de sauvegarde',
    'pitr.periodUnknown': 'base en service inconnue',
    'pitr.periodsFuture':
      'Chaque nouvelle base reçoit désormais une sauvegarde complète dès sa création : ces trous ne se reproduiront plus.',
    'pitr.legendGeneration': 'g{{generation}}',
    'pitr.legendCurrent': 'g{{generation}} (en service)',
    'pitr.legendGap': 'non restaurable',
    'pitr.legendIcons':
      '◇ instantané · ● sauvegarde complète · ■ configuration · ▲ déploiement',
    'pitr.markerSwitch': 'Bascule vers la génération {{generation}} · {{time}}',
    'pitr.hourCellGap': '{{hour}} h, non restaurable : {{reason}}',
    'pitr.refusalNotRestorable': 'Non restaurable : {{reason}}',
    'pitr.gapNoBarman':
      'aucune sauvegarde complète (Barman) de la génération {{generation}} ne couvre cette heure ; ses instantanés ont disparu avec elle.',
    'pitr.gapSwitch':
      'la base venait d’être remplacée par une restauration et n’avait pas encore de sauvegarde.',
    'pitr.gapNoBackup':
      'aucune sauvegarde de la base n’existe avant cette heure.',
    'pitr.gapEndUnsure':
      'fin de période non garantie : les journaux de cette base peuvent ne pas atteindre cette heure (juste avant sa bascule).',
    'pitr.eventsTruncated':
      'Trop de déploiements et de changements de configuration : les plus anciens ne sont pas affichés.',
    'pitr.gapUnknown': 'la base en service à cette heure est inconnue.',
  },
});

export const projectEnvironmentsFrTranslation = TranslationBlueprint.make({
  name: 'project-environments-fr',
  params: { resource },
});
